import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ncfAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext.jsx';
import NcfReviewRequestModal from '../components/ncf/NcfReviewRequestModal.jsx';
import NcfChangeModal from '../components/ncf/NcfChangeModal.jsx';
import NcfContractEndModal from '../components/ncf/NcfContractEndModal.jsx';
import NcfPlacementWorksheet, { WORKSHEET_PATHS } from '../components/ncf/NcfPlacementWorksheet.jsx';
import { applyPlacementCalculation, synchronizePlacementInputs, getValue, financialSignature, isPermanentPlacement, ratesLayout, PLACEMENT_DERIVED_PATHS, FEE_PERCENT, FEE_PERCENT_UNIT, FEE_MODE, FEE_AMOUNT } from '../../../backend/src/services/ncfFinancials.mjs';
import { getFieldValidationError, getInvalidContactFields } from '../../../backend/src/services/ncfFieldValidation.mjs';
import { applyReviewedValues } from '../../../backend/src/services/ncfJobChange.mjs';
import AddressInput from '../components/ncf/AddressInput.jsx';
import { describeAddressIssue, validateAddress } from '../utils/addressValidation.js';
import { C2C_LINKED_NAME_PATH, C2C_OWNERSHIP_PATH, NCF_CHANGE_STATUSES, applySubmissionMetadataDefaults, buildNcfReviewRoot, linkedCorporationPatch } from '../utils/ncfReviewRoot.js';
import NcfSameAsManager from '../components/ncf/NcfSameAsManager.jsx';
import { ncfPathFromRecord, parseNcfId } from '../utils/ncfSlug.js';
import { clearBreadcrumbTitle, setBreadcrumbTitle } from '../utils/breadcrumbTitle.js';
import './NCF.css';
import Typeahead from '../components/Typeahead';
import { evaluateRequirementRules, getSimpleEqualityCondition, isFieldEffectivelyRequired } from '../utils/ncfRequirementRules.js';


const pathParts = (path) => String(path || '').replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
const getPath = (source, path) => pathParts(path).reduce((value, key) => value?.[key], source) ?? '';
const formatRateUnit = (value) => {
  const unit = String(value || 'hourly').trim().toLowerCase();
  if (['year', 'yearly', 'annual', 'annually', 'salary'].includes(unit)) return 'per year';
  if (['hour', 'hourly', 'hr'].includes(unit)) return 'per hour';
  return unit.startsWith('per ') ? unit : `per ${unit}`;
};
// Bill/pay/OT rate fields are pulled out of the normal Client/Project Information grid and
// rendered together in a "Rates" panel so each rate lines up with its frequency unit.
// OT rates share the same frequency as their base rate (JobDiva stores one unit per pair).
// Matched by label or last path segment (not full source path) so admins can repoint a rate's source.
const RATE_KINDS = [
  { kind: 'bill', suffixes: ['billrate'], perPath: 'jobdiva.job.billRatePer' },
  { kind: 'otBill', suffixes: ['otbillrate', 'overtimebillrate'], perPath: 'jobdiva.job.billRatePer' },
  { kind: 'pay', suffixes: ['payrate'], perPath: 'assignment.payRateType' },
  { kind: 'otPay', suffixes: ['otpayrate', 'overtimepayrate'], perPath: 'assignment.payRateType' }
];
const compactText = (value) => String(value || '').toLowerCase().replace(/[^a-z]/g, '');
const getRateKind = (field) => {
  const candidates = [compactText(pathParts(field.sourcePath).pop()), compactText(field.label)];
  const matches = (suffix) => candidates.some(text => text.endsWith(suffix));
  // Check OT variants first because "otbillrate" also ends with "billrate".
  return [RATE_KINDS[1], RATE_KINDS[3], RATE_KINDS[0], RATE_KINDS[2]]
    .find(rateKind => rateKind.suffixes.some(matches)) || null;
};
const RATE_PER_OPTIONS = ['hour', 'day', 'week', 'month', 'year'];
const REVIEW_NOT_REQUIRED_TEXT = 'Review not required.';
const TYPED_EMAIL_PATTERN = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]+$/;
const setPath = (source, path, value) => {
  const keys = pathParts(path);
  let target = source;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) target[key] = value;
    else {
      const nextKey = keys[index + 1];
      target[key] = target[key] || (/^\d+$/.test(nextKey) ? [] : {});
      target = target[key];
    }
  });
};

// Keep in sync with findStartDateFieldPath in backend ncfService.js.
const START_DATE_NAMES = new Set(['startdate', 'jobstartdate', 'consultantstartdate', 'assignmentstartdate']);
const findStartDateFieldPath = (fields) => {
  const candidates = fields.filter(field => field.isActive !== false
    && !/^(employmentHistory|education|references)\b/.test(field.sourcePath)
    && (START_DATE_NAMES.has(compactText(pathParts(field.sourcePath).pop())) || START_DATE_NAMES.has(compactText(field.label))));
  return (candidates.find(field => field.sourcePath === 'assignment.jobStartDate') || candidates[0])?.sourcePath
    || 'assignment.jobStartDate';
};

const buildJobDivaAddressRoot = (ncf) => {
  const candidate = ncf?.jobDivaData?.candidate || {};
  return {
    contact: candidate.contact || {},
    emergencyContact: candidate.emergencyContact || {},
    references: Array.isArray(candidate.references) ? candidate.references : [],
    jobdiva: ncf?.jobDivaData || {}
  };
};

const addressReadinessIssue = (fields) => ({
  key: 'addresses',
  title: 'Addresses are incomplete',
  message: 'Enter full addresses (street number and name, city, state/province where applicable, and postal code) before submitting to HR.',
  fields
});

const isValidDateValue = (value) => {
  if (value == null || String(value).trim() === '') return false;
  const text = String(value).trim();
  if (/^mm\/dd\/yyyy$/i.test(text)) return false;
  const parsed = new Date(text);
  return !Number.isNaN(parsed.getTime());
};

const toLocalDateOnly = (value) => {
  if (value == null || String(value).trim() === '') return null;
  const text = String(value).trim();
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch.map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const isPastDateValue = (value) => {
  const date = toLocalDateOnly(value);
  if (!date) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return date < today;
};

const isContractEndDatePast = (value) => {
  const date = toLocalDateOnly(value);
  const today = toLocalDateOnly(new Date().toISOString());
  return Boolean(date && today && date < today);
};

const isDateBeforeValue = (value, comparisonValue) => {
  const date = toLocalDateOnly(value);
  const comparisonDate = toLocalDateOnly(comparisonValue);
  return Boolean(date && comparisonDate && date < comparisonDate);
};

const toDateInputValue = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toISOString().slice(0, 10) : '';
};

const formatDisplayDate = (value) => {
  if (!value) return '-';
  // Pure date strings are UTC calendar dates; parse as local to avoid a one-day-back shift.
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch.map(Number);
    return new Date(year, month - 1, day).toLocaleDateString();
  }
  return new Date(value).toLocaleDateString();
};

const applyNonEmptyPaths = (target, source) => {
  if (!source || typeof source !== 'object') return target;
  const visit = (value, parts) => {
    if (Array.isArray(value)) {
      if (value.length > 0) setPath(target, parts.join('.'), value);
      return;
    }
    if (value && typeof value === 'object') {
      Object.entries(value).forEach(([key, child]) => visit(child, [...parts, key]));
      return;
    }
    if (value != null && String(value).trim() !== '') {
      setPath(target, parts.join('.'), value);
    }
  };
  Object.entries(source).forEach(([key, value]) => visit(value, [key]));
  return target;
};

function NcfDetail() {
  const { id: routeParam } = useParams();
  const id = parseNcfId(routeParam);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('');
  const [statusDefinitions, setStatusDefinitions] = useState([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [submittingToHr, setSubmittingToHr] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('review');
  const [shareRecipients, setShareRecipients] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [documentType, setDocumentType] = useState('resume');
  const [resumeUploadModal, setResumeUploadModal] = useState(false);
  const [resumeUploadFile, setResumeUploadFile] = useState(null);
  const [resumeUploadBusy, setResumeUploadBusy] = useState(false);
  const [jobDivaResumeLoading, setJobDivaResumeLoading] = useState(false);
  const jobDivaResumeAttemptedRef = useRef(false);
  const [reviewData, setReviewData] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({});
  const [fieldInventory, setFieldInventory] = useState([]);
  const [sectionVisibility, setSectionVisibility] = useState([]);
  const [submissionVersions, setSubmissionVersions] = useState([]);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [changeModalOpen, setChangeModalOpen] = useState(false);
  const [jobDivaAddressChecks, setJobDivaAddressChecks] = useState([]);
  const [viewingChanges, setViewingChanges] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
  const [noticeModal, setNoticeModal] = useState({ show: false, title: 'Notice', message: '' });
  const [submitReadinessModal, setSubmitReadinessModal] = useState({
    show: false,
    blockingIssues: [],
    overrideIssues: [],
    recipients: [],
    recipientsLoading: false,
    error: '',
    resumeFile: null,
    resumeUploading: false,
    resumeValidated: false,
    reviewOverrideReason: '',
    startDateOverrideAcknowledged: false,
    startDateValue: '',
    startDateSaved: false,
    submitterComment: ''
  });
  const [missingFieldsModal, setMissingFieldsModal] = useState({ show: false, fields: [] });
  const [approvalWorkflow, setApprovalWorkflow] = useState({ enabled: false, workflow: null, approvals: [] });
  const [approvalReviewers, setApprovalReviewers] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalRequestModal, setApprovalRequestModal] = useState(false);
  const [reviewRequestModalOpen, setReviewRequestModalOpen] = useState(false);
  const [approvalSelections, setApprovalSelections] = useState({ recruiter: '', salesperson: '' });
  const [approvalRoleEnabled, setApprovalRoleEnabled] = useState({ recruiter: false, salesperson: false });
  const [reviewNotNecessary, setReviewNotNecessary] = useState(false);
  const [reviewRequestError, setReviewRequestError] = useState('');
  const [additionalApprovalRecipients, setAdditionalApprovalRecipients] = useState([]);
  const [approvalCustomMessage, setApprovalCustomMessage] = useState('');
  const [reviewSendConfirmation, setReviewSendConfirmation] = useState('');
  const approvalSelectionsBackupRef = useRef({ recruiter: '', salesperson: '' });
  const additionalApprovalRecipientsBackupRef = useRef([]);
  const [approvalDecisionModal, setApprovalDecisionModal] = useState(null);
  const [approvalDecisionNote, setApprovalDecisionNote] = useState('');
  const [approvalOverrideModal, setApprovalOverrideModal] = useState(false);
  const [approvalOverrideReason, setApprovalOverrideReason] = useState('');
  const [reviewGateModal, setReviewGateModal] = useState({ show: false, reason: null });
  const [hrNotificationRecipients, setHrNotificationRecipients] = useState([]);
  const [hrRecipientsLoading, setHrRecipientsLoading] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const reviewDataReadyRef = useRef(false);
  const reviewDataDirtyRef = useRef(false);
  const reviewDataVersionRef = useRef(0);
  const autoSaveTimerRef = useRef(null);
  const [overviewStartDateEditing, setOverviewStartDateEditing] = useState(false);
  const [overviewStartDateValue, setOverviewStartDateValue] = useState('');
  const [overviewStartDateBusy, setOverviewStartDateBusy] = useState(false);
  const [contractEndModal, setContractEndModal] = useState({ show: false, value: '', reason: '', busy: false, error: '' });
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState('');
  const [commentRecipientRoles, setCommentRecipientRoles] = useState({ recruiter: false, salesperson: false, submitter: false, hr: false });
  const [commentAdditionalRecipients, setCommentAdditionalRecipients] = useState([]);
  const [commentBusy, setCommentBusy] = useState(false);
  const persistedStatus = data?.ncf?.Status || data?.ncf?.status || status;
  const currentUserId = user?.userId || user?.UserId || user?.id || '';
  const canManageAny = hasPermission('ncf:write:any');
  const canWriteThisNcf = canManageAny || (
    hasPermission('ncf:write:own')
    && String(data?.ncf?.userId || data?.ncf?.UserId || '') === String(currentUserId)
  );
  const canReadDocuments = hasPermission('ncf:read:any') || (
    hasPermission('ncf:read:own')
    && String(data?.ncf?.userId || data?.ncf?.UserId || '') === String(currentUserId)
  );
  const canApproveAssigned = hasPermission('ncf:approve:assigned');

  const load = async () => {
    try {
      const response = await ncfAPI.get(id);
      setData(response.data);
      const storedReview = response.data.ncf?.reviewedData;
      const merged = response.data.ncf?.mergedData?.data || response.data.ncf?.mergedData;
      const extracted = response.data.ncf?.extractedData;
      reviewDataReadyRef.current = false;
      reviewDataDirtyRef.current = false;
      setReviewData(storedReview || merged || extracted || {});
      reviewDataReadyRef.current = true;
      setStatus(response.data.ncf?.Status || response.data.ncf?.status || 'draft');
      try {
        const submissionsResponse = await ncfAPI.submissions(id);
        setSubmissionVersions(submissionsResponse.data.submissions || []);
      } catch (_error) {
        setSubmissionVersions([]);
      }
      try {
        const notificationsResponse = await ncfAPI.notificationLogs(id);
        setNotificationLogs(notificationsResponse.data.notifications || []);
      } catch (_error) {
        setNotificationLogs([]);
      }
      try {
        const commentsResponse = await ncfAPI.comments(id);
        setComments(commentsResponse.data.comments || []);
      } catch (_error) {
        setComments([]);
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load this NCF.');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    ncfAPI.statuses().then(response => setStatusDefinitions(response.data.statuses || [])).catch(() => setStatusDefinitions([]));
  }, []);

  // The Client/Project Information "Start Date" field (assignment.jobStartDate) is the
  // candidate's authoritative start date; syncing it here keeps NcfCandidates.DesiredStartDate
  // (History page column + verification/reminder workflow) aligned with what's on the form.
  const syncCandidateStartDate = async (value) => {
    if (!isValidDateValue(value)) return;
    try {
      const response = await ncfAPI.updateStartDate(id, value);
      setData(previous => ({ ...previous, ncf: { ...previous.ncf, ...response.data.ncf } }));
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not update the start date.');
    }
  };

  const loadApprovalWorkflow = async () => {
    setApprovalLoading(true);
    try {
      const response = await ncfAPI.approval(id);
      const workflow = response.data.workflow || { enabled: false, workflow: null, approvals: [] };
      setApprovalWorkflow(workflow);
      setAdditionalApprovalRecipients(workflow.workflow?.additionalRecipients || []);
      additionalApprovalRecipientsBackupRef.current = workflow.workflow?.additionalRecipients || [];
      setApprovalCustomMessage(workflow.workflow?.customMessage || '');

      const existingSelections = workflow.approvals.reduce((selection, approval) => ({
        ...selection,
        [approval.role]: approval.reviewerUserId
      }), {});
      setApprovalSelections({
        recruiter: existingSelections.recruiter || '',
        salesperson: existingSelections.salesperson || ''
      });
      setApprovalRoleEnabled({
        recruiter: Boolean(existingSelections.recruiter),
        salesperson: Boolean(existingSelections.salesperson)
      });

      const canConfigureReview = canWriteThisNcf;
      if (!canConfigureReview) {
        setApprovalReviewers([]);
        setHrNotificationRecipients([]);
        return;
      }

      const [reviewersResponse, defaultsResponse, hrRecipientsResponse] = await Promise.all([
        ncfAPI.reviewers(),
        ncfAPI.reviewerDefaults(id),
        ncfAPI.hrNotificationRecipients()
      ]);
      setHrNotificationRecipients(hrRecipientsResponse.data.recipients || []);
      const defaults = defaultsResponse.data.defaults || {};
      const directoryReviewers = reviewersResponse.data.reviewers || [];
      const reviewersWithDefaults = [...directoryReviewers];
      [defaults.recruiter, defaults.salesperson].filter(Boolean).forEach(defaultReviewer => {
        if (!reviewersWithDefaults.some(reviewer => String(reviewer.userId) === String(defaultReviewer.userId))) {
          reviewersWithDefaults.push(defaultReviewer);
        }
      });
      setApprovalReviewers(reviewersWithDefaults);
      setApprovalSelections({
        recruiter: existingSelections.recruiter || defaults.recruiter?.userId || '',
        salesperson: existingSelections.salesperson || defaults.salesperson?.userId || ''
      });
      setApprovalRoleEnabled({
        recruiter: Boolean(existingSelections.recruiter || defaults.recruiter?.userId),
        salesperson: Boolean(existingSelections.salesperson || defaults.salesperson?.userId)
      });
      approvalSelectionsBackupRef.current = {
        recruiter: existingSelections.recruiter || defaults.recruiter?.userId || '',
        salesperson: existingSelections.salesperson || defaults.salesperson?.userId || ''
      };
      setReviewNotNecessary(false);
    } catch (_error) {
      setApprovalWorkflow({ enabled: false, workflow: null, approvals: [] });
      setApprovalReviewers([]);
      setApprovalSelections({ recruiter: '', salesperson: '' });
      setApprovalRoleEnabled({ recruiter: false, salesperson: false });
      approvalSelectionsBackupRef.current = { recruiter: '', salesperson: '' };
      setReviewNotNecessary(false);
      setAdditionalApprovalRecipients([]);
      additionalApprovalRecipientsBackupRef.current = [];
      setApprovalCustomMessage('');
      setHrNotificationRecipients([]);
    } finally { setApprovalLoading(false); }
  };

  // Prefers the actually-assigned reviewer (workflow) over the record-linked default, matching
  // whoever the recruiter/salesperson checkbox would actually notify.
  const getCommentReviewerInfo = (role) => {
    const approval = approvalWorkflow.approvals?.find(item => item.role === role);
    if (approval) return { name: approval.reviewerName, email: approval.reviewerEmail };
    const reviewer = approvalReviewers.find(item => String(item.userId) === String(approvalSelections[role]));
    return reviewer ? { name: reviewer.name, email: reviewer.email } : null;
  };

  const searchApprovalRecipients = useCallback(async (query, { signal } = {}) => {
    const response = await ncfAPI.searchRecipientDirectory(query, { signal });
    return (response.data.recipients || []).map(item => ({
      id: item.id,
      label: item.name || item.email,
      sublabel: `${item.email} • ${item.type === 'group' ? 'Group' : 'User'}`,
      raw: item
    }));
  }, []);

  const createTypedEmailRecipient = useCallback((query) => {
    const email = String(query || '').trim().toLowerCase();
    if (!TYPED_EMAIL_PATTERN.test(email)) return null;
    return { id: `typed:${email}`, label: `Add ${email}`, sublabel: 'Typed email address', raw: { id: null, name: email, email, type: 'user' } };
  }, []);

  useEffect(() => {
    if (reviewRequestModalOpen || approvalRequestModal) setReviewRequestError('');
  }, [reviewRequestModalOpen, approvalRequestModal]);

  useEffect(() => {
    if (Object.values(approvalSelections).some(Boolean)) approvalSelectionsBackupRef.current = approvalSelections;
    if (additionalApprovalRecipients.length > 0) additionalApprovalRecipientsBackupRef.current = additionalApprovalRecipients;
  }, [approvalSelections, additionalApprovalRecipients]);

  useEffect(() => {
    if (!approvalRequestModal) return;
    setApprovalRequestModal(false);
    setReviewRequestModalOpen(true);
  }, [approvalRequestModal]);

  const loadFieldInventory = async () => {
    try {
      const response = await ncfAPI.fields();
      setFieldInventory(response.data.fields || []);
      setSectionVisibility(response.data.sectionVisibility || []);
    } catch (err) {
      // Inventory load failure is non-fatal; fall back to hardcoded required flags.
    }
  };

  useEffect(() => {
    jobDivaResumeAttemptedRef.current = false;
    load();
    loadFieldInventory();
  }, [id]);

  useEffect(() => {
    const loadedNcf = data?.ncf;
    if (!loadedNcf || String(loadedNcf.ncfId || loadedNcf.NcfId) !== String(id)) return undefined;
    const canonicalPath = ncfPathFromRecord(loadedNcf);
    if (location.pathname !== canonicalPath) {
      navigate({ pathname: canonicalPath, search: location.search, hash: location.hash }, { replace: true });
      return undefined;
    }
    const lastName = loadedNcf.candidateLastName || loadedNcf.CandidateLastName;
    const clientName = loadedNcf.clientName || loadedNcf.ClientName;
    const jobTitle = loadedNcf.jobDivaJobTitle || loadedNcf.JobDivaJobTitle;
    const title = [[lastName, clientName].filter(Boolean).join(' — '), jobTitle].filter(Boolean).join(' · ') || `NCF ${id}`;
    setBreadcrumbTitle(canonicalPath, title);
    const previousDocumentTitle = document.title;
    document.title = `${title} | NCF`;
    return () => {
      clearBreadcrumbTitle(canonicalPath);
      document.title = previousDocumentTitle;
    };
  }, [data?.ncf, id, location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    const addressFields = fieldInventory.filter(field => field.format === 'address');
    if (!data?.ncf || addressFields.length === 0) {
      setJobDivaAddressChecks([]);
      return undefined;
    }
    const jobDivaRoot = buildJobDivaAddressRoot(data.ncf);
    const candidates = addressFields
      .map(field => ({ field, value: String(getPath(jobDivaRoot, field.sourcePath) ?? '').trim() }))
      .filter(item => item.value);
    let active = true;
    Promise.all(candidates.map(item => validateAddress(item.value))).then(results => {
      if (!active) return;
      setJobDivaAddressChecks(candidates
        .map((item, index) => ({ ...item, result: results[index] }))
        .filter(item => item.result && !item.result.complete));
    });
    return () => { active = false; };
  }, [data?.ncf, fieldInventory]);

  useEffect(() => {
    const loadedNcfId = data?.ncf?.ncfId || data?.ncf?.NcfId;
    if (!loadedNcfId || String(loadedNcfId) !== String(id)) return;
    loadApprovalWorkflow();
  }, [id, data?.ncf?.ncfId, data?.ncf?.NcfId, canWriteThisNcf]);

  useEffect(() => {
    const candidateId = data?.ncf?.jobDivaCandidateId || data?.ncf?.JobDivaCandidateId;
    const documents = data?.documents || [];
    const hasResume = documents.some(document => String(document.documentType || document.DocumentType || '').toLowerCase() === 'resume');
    if (!canWriteThisNcf || activeTab !== 'documents' || !candidateId || hasResume || jobDivaResumeAttemptedRef.current) return;

    jobDivaResumeAttemptedRef.current = true;
    setJobDivaResumeLoading(true);
    ncfAPI.retrieveJobDivaResume(id)
      .then(response => {
        setData(previous => ({ ...previous, documents: response.data.documents || previous.documents }));
      })
      .catch(() => {})
      .finally(() => setJobDivaResumeLoading(false));
  }, [activeTab, canWriteThisNcf, data, id]);

  useEffect(() => {
    const canAutoSave = persistedStatus === 'draft' || (canManageAny && persistedStatus !== 'cancelled');
    if (!reviewDataReadyRef.current || !reviewDataDirtyRef.current || !canAutoSave) return undefined;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaveStatus('Saving...');
    const version = reviewDataVersionRef.current;
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        await ncfAPI.saveReview(id, reviewData);
        if (reviewDataVersionRef.current === version) {
          reviewDataDirtyRef.current = false;
          setAutoSaveStatus('Saved');
        }
      } catch (_error) {
        setAutoSaveStatus('Save failed');
      }
    }, 900);
    return () => clearTimeout(autoSaveTimerRef.current);
  }, [id, reviewData, persistedStatus, canManageAny]);

  const updateStatus = async () => {
    setBusy(true);
    setError('');
    try {
      const response = await ncfAPI.updateStatus(id, status, note);
      const notification = response.data?.notification;
      setMessage(notification?.failedCount > 0
        ? `Status updated and comment added. ${notification.failedCount} notification${notification.failedCount === 1 ? '' : 's'} could not be delivered.`
        : notification
          ? `Status updated, comment added, and ${notification.sentCount} notification${notification.sentCount === 1 ? '' : 's'} sent.`
          : 'Status updated.');
      await load();
      setNote('');
    }
    catch (err) { setError(err?.response?.data?.error || 'Could not update status.'); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const response = await ncfAPI.generate(id);
      const generatedName = response.data?.fileName || 'NCF workbook';
      const writtenCount = response.data?.writtenCount ?? 0;
      const warningCount = response.data?.warnings?.length || 0;
      setMessage(`${generatedName} generated with ${writtenCount} populated field(s)${warningCount ? ` and ${warningCount} warning(s)` : ''}.`);
      await load();
    }
    catch (err) { setError(err?.response?.data?.error || 'Could not generate the workbook.'); }
    finally { setBusy(false); }
  };

  const saveReview = async () => {
    setBusy(true);
    try { await ncfAPI.saveReview(id, reviewData); reviewDataDirtyRef.current = false; setAutoSaveStatus('Saved'); await load(); }
    catch (err) { setError(err?.response?.data?.error || 'Could not save candidate data.'); }
    finally { setBusy(false); }
  };

  const requestApprovals = async () => {
    setError('');
    const selectedApprovals = Object.values(approvalSelections).some(Boolean) ? approvalSelections : approvalSelectionsBackupRef.current;
    const selectedAdditionalRecipients = additionalApprovalRecipients.length > 0
      ? additionalApprovalRecipients
      : additionalApprovalRecipientsBackupRef.current;
    const approvals = reviewNotNecessary ? [] : Object.entries(selectedApprovals)
      .filter(([role]) => approvalRoleEnabled[role])
      .filter(([, userId]) => userId)
      .map(([role, userId]) => ({ role, userId }));
    const additionalRecipients = reviewNotNecessary ? [] : selectedAdditionalRecipients;
    if (!reviewNotNecessary && approvals.length === 0 && additionalRecipients.length === 0) {
      setReviewRequestError('Select at least one reviewer or add a recipient, or check "Review not necessary".');
      return;
    }
    setReviewRequestError('');
    setBusy(true);
    try {
      const response = await ncfAPI.requestApproval(id, approvals, additionalRecipients, approvalCustomMessage, reviewNotNecessary);
      setApprovalWorkflow(response.data.workflow);
      setApprovalRequestModal(false);
      setReviewRequestModalOpen(false);
      const confirmation = approvals.length > 0 || additionalRecipients.length > 0
        ? 'Review notification sent.'
        : 'Review not required. NCF can be submitted directly to HR.';
      setReviewSendConfirmation(confirmation);
      setMessage(confirmation);
      setApprovalCustomMessage('');
      const notificationsResponse = await ncfAPI.notificationLogs(id);
      setNotificationLogs(notificationsResponse.data.notifications || []);
    } catch (err) {
      const missing = err?.response?.data?.missingFields;
      if (err?.response?.data?.code === 'NCF_MISSING_REQUIRED_FIELDS' && missing?.length) setMissingFieldsModal({ show: true, fields: missing });
      else setReviewRequestError(err?.response?.data?.error || 'Could not send the NCF for approval.');
    } finally { setBusy(false); }
  };

  const addAdditionalApprovalRecipient = (option) => {
    if (!option?.raw || additionalApprovalRecipients.some(item => item.email.toLowerCase() === option.raw.email.toLowerCase())) return;
    setAdditionalApprovalRecipients(previous => [...previous, option.raw]);
  };

  const decideApproval = async () => {
    if (!approvalDecisionModal) return;
    if (approvalDecisionModal.decision === 'rejected' && !approvalDecisionNote.trim()) {
      setError('A rejection reason is required.');
      return;
    }
    setBusy(true);
    try {
      const response = await ncfAPI.decideApproval(id, approvalDecisionModal.role, approvalDecisionModal.decision, approvalDecisionNote);
      setApprovalWorkflow(response.data.workflow);
      setApprovalDecisionModal(null);
      setApprovalDecisionNote('');
      setMessage(`Approval ${approvalDecisionModal.decision}.`);
    } catch (err) { setError(err?.response?.data?.error || 'Could not update the approval.'); }
    finally { setBusy(false); }
  };

  const overrideAndSubmit = async () => {
    if (!approvalOverrideReason.trim()) {
      setError('An override reason is required.');
      return;
    }
    setBusy(true);
    try {
      const response = await ncfAPI.overrideApproval(id, approvalOverrideReason, { signature: reviewedFinancialSignature });
      setApprovalOverrideModal(false);
      setApprovalOverrideReason('');
      await load();
      setNoticeModal({ show: true, title: 'Submitted to HR', message: response.data?.notification?.sentCount ? 'NCF submitted and HR was notified.' : 'NCF submitted to HR.' });
    } catch (err) {
      const missing = err?.response?.data?.missingFields;
      if (err?.response?.data?.code === 'NCF_RESUME_REQUIRED') {
        setApprovalOverrideModal(false);
        setResumeUploadModal(true);
      } else if (err?.response?.data?.code === 'NCF_MISSING_REQUIRED_FIELDS' && missing?.length) {
        setApprovalOverrideModal(false);
        setMissingFieldsModal({ show: true, fields: missing });
      }
      else setError(err?.response?.data?.error || 'Could not override approvals.');
    } finally { setBusy(false); }
  };

  const openApprovalOverrideModal = async () => {
    setHrRecipientsLoading(true);
    try {
      const response = await ncfAPI.hrNotificationRecipients();
      setHrNotificationRecipients(response.data.recipients || []);
      setApprovalOverrideModal(true);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not resolve HR notification recipients.');
    } finally { setHrRecipientsLoading(false); }
  };

  const getReviewReadinessIssue = () => {
    if (approvalLoading) return null;
    if (approvalWorkflow.reviewStatus === 'not_configured') {
      return {
        key: 'review',
        title: 'Review has not been sent',
        message: 'This NCF has not been sent for recruiter/salesperson review yet.',
        allowNotRequired: true
      };
    }
    if (approvalsComplete) return null;
    if (approvalWorkflow.enabled) {
      const pendingReviewers = approvalWorkflow.approvals
        .filter(approval => approval.status !== 'approved')
        .map(approval => approval.reviewerName || reviewerRoleLabel(approval.role));
      return {
        key: 'review',
        title: 'Review is not complete',
        message: pendingReviewers.length > 0
          ? `Still waiting on ${pendingReviewers.join(', ')}.`
          : 'One or more reviewers has not approved this NCF yet.'
      };
    }
    return null;
  };

  const buildSubmitReadiness = (resumeValidated = false) => {
    const hasResume = resumeValidated || documents.some(document => String(document.documentType || document.DocumentType || '').toLowerCase() === 'resume');
    const missingFields = getMissingRequiredFields();
    const blockingIssues = [];
    const overrideIssues = [];
    const invalidFields = getInvalidContactFields(visibleReviewSections.flatMap(section => section.fields), reviewRoot);
    if (invalidFields.length > 0) {
      blockingIssues.push({
        key: 'contact-fields',
        title: 'Email addresses or phone numbers are invalid',
        message: 'Correct these fields before submitting to HR.',
        fields: invalidFields.map(field => ({ ...field, fieldGroup: field.message }))
      });
    }

    if (!ratesAcknowledged) blockingIssues.push({ key: 'rates-review', title: 'Rates review is required', message: 'Review the rates, frequencies and placement fees below, then check the acknowledgement.' });

    if (missingFields.length > 0) {
      blockingIssues.push({
        key: 'fields',
        title: 'Required fields are incomplete',
        message: 'Fill in these required fields before submitting to HR.',
        fields: missingFields
      });
    }

    if (!hasResume) {
      blockingIssues.push({
        key: 'resume',
        title: 'Candidate resume is missing',
        message: 'Upload the candidate resume before submitting this NCF to HR.'
      });
    }

    const reviewIssue = getReviewReadinessIssue();
    if (reviewIssue) overrideIssues.push(reviewIssue);

    if (startDateNeedsReview(ncfStartDateValue)) overrideIssues.push(startDateReadinessIssue(ncfStartDateValue));

    return { blockingIssues, overrideIssues };
  };

  const openSubmitReadinessModal = async ({ resumeValidated = false } = {}) => {
    setError('');
    const readiness = buildSubmitReadiness(resumeValidated);
    const incompleteAddresses = await getIncompleteAddresses();
    if (incompleteAddresses.length > 0) readiness.blockingIssues.push(addressReadinessIssue(incompleteAddresses));
    const hasBlockingIssues = readiness.blockingIssues.length > 0;
    setSubmitReadinessModal(previous => ({
      ...previous,
      show: true,
      ...readiness,
      recipients: [],
      recipientsLoading: !hasBlockingIssues,
      error: '',
      resumeFile: null,
      resumeUploading: false,
      resumeValidated,
      reviewOverrideReason: previous.reviewOverrideReason,
      startDateOverrideAcknowledged: previous.startDateOverrideAcknowledged && readiness.overrideIssues.some(issue => issue.key === 'start-date'),
      startDateValue: toDateInputValue(ncfStartDateValue),
      startDateSaved: false,
      submitterComment: previous.submitterComment
    }));

    if (hasBlockingIssues) return;
    await loadSubmitReadinessRecipients();
  };

  // The rates acknowledgement can be checked from inside the modal, so that blocker is resolved live.
  const getActiveBlockingIssues = (modalState = submitReadinessModal) => modalState.blockingIssues
    .filter(issue => !(issue.key === 'rates-review' && ratesAcknowledged));

  const acknowledgeRatesFromReadiness = async (checked) => {
    updateFinancialFields({
      'ncf.ratesReviewSignature': checked ? currentFinancialSignature : '',
      'ncf.ratesReviewUserId': checked ? String(currentUserId) : ''
    });
    const otherBlockingIssues = submitReadinessModal.blockingIssues.filter(issue => issue.key !== 'rates-review');
    if (checked && otherBlockingIssues.length === 0 && submitReadinessModal.recipients.length === 0) {
      setSubmitReadinessModal(previous => ({ ...previous, recipientsLoading: true, error: '' }));
      await loadSubmitReadinessRecipients();
    }
  };

  const toggleReviewNotRequired = (checked) => {
    setSubmitReadinessModal(previous => {
      const reason = previous.reviewOverrideReason.replace(REVIEW_NOT_REQUIRED_TEXT, '').trim();
      return {
        ...previous,
        reviewOverrideReason: checked ? [REVIEW_NOT_REQUIRED_TEXT, reason].filter(Boolean).join('\n') : reason,
        error: ''
      };
    });
  };

  const loadSubmitReadinessRecipients = async () => {
    try {
      const response = await ncfAPI.hrNotificationRecipients();
      const recipients = response.data.recipients || [];
      setHrNotificationRecipients(recipients);
      setSubmitReadinessModal(previous => ({ ...previous, recipients, recipientsLoading: false }));
    } catch (err) {
      setSubmitReadinessModal(previous => ({
        ...previous,
        recipientsLoading: false,
        error: err?.response?.data?.error || 'Could not resolve HR notification recipients.'
      }));
    }
  };

  const submitToHr = async () => {
    await openSubmitReadinessModal();
  };

  const uploadResumeFromReadiness = async () => {
    if (!submitReadinessModal.resumeFile) {
      setSubmitReadinessModal(previous => ({ ...previous, error: 'Choose a candidate resume to upload.' }));
      return;
    }

    setSubmitReadinessModal(previous => ({ ...previous, resumeUploading: true, error: '' }));
    try {
      const response = await ncfAPI.addDocument(id, submitReadinessModal.resumeFile, 'resume');
      setData(previous => ({ ...previous, documents: response.data.documents || previous.documents }));
      await openSubmitReadinessModal({ resumeValidated: true });
    } catch (err) {
      setSubmitReadinessModal(previous => ({
        ...previous,
        resumeUploading: false,
        error: err?.response?.data?.error || 'Could not upload the candidate resume.'
      }));
    }
  };

  const submitFromReadinessModal = async () => {
    const hasReviewOverride = submitReadinessModal.overrideIssues.some(issue => issue.key === 'review');
    const hasStartDateOverride = submitReadinessModal.overrideIssues.some(issue => issue.key === 'start-date');
    if (getActiveBlockingIssues().length > 0 || submitReadinessModal.recipientsLoading) return;
    if (hasReviewOverride && !submitReadinessModal.reviewOverrideReason.trim()) {
      setSubmitReadinessModal(previous => ({ ...previous, error: 'Enter a reason for submitting without completed review.' }));
      return;
    }
    if (hasStartDateOverride && !submitReadinessModal.startDateOverrideAcknowledged) {
      setSubmitReadinessModal(previous => ({ ...previous, error: 'Acknowledge the start date warning before submitting.' }));
      return;
    }

    setBusy(true);
    setSubmittingToHr(true);
    setSubmitReadinessModal(previous => ({ ...previous, error: '' }));
    try {
      await ncfAPI.saveReview(id, reviewData);
      const response = hasReviewOverride
        ? await ncfAPI.overrideApproval(id, submitReadinessModal.reviewOverrideReason.trim(), { signature: reviewedFinancialSignature }, submitReadinessModal.submitterComment)
        : await ncfAPI.submitToHr(id, { signature: reviewedFinancialSignature }, submitReadinessModal.submitterComment);
      const notification = response.data?.notification;
      setSubmitReadinessModal(previous => ({ ...previous, show: false }));
      await load();
      setNoticeModal({
        show: true,
        title: 'Submitted to HR',
        message: notification?.failedCount
          ? `${notification.sentCount} notification(s) sent; ${notification.failedCount} failed.`
          : `${notification?.sentCount || 0} HR notification(s) sent.`
      });
    } catch (err) {
      const serverMissingFields = err?.response?.data?.missingFields;
      if (err?.response?.data?.code === 'NCF_RESUME_REQUIRED') {
        setSubmitReadinessModal(previous => ({
          ...previous,
          blockingIssues: [{ key: 'resume', title: 'Candidate resume is missing', message: 'Upload the candidate resume before submitting this NCF to HR.' }],
          error: ''
        }));
      } else if (err?.response?.data?.code === 'NCF_REVIEW_STATUS_REQUIRED') {
        setSubmitReadinessModal(previous => ({
          ...previous,
          overrideIssues: previous.overrideIssues.some(issue => issue.key === 'review')
            ? previous.overrideIssues
            : [...previous.overrideIssues, { key: 'review', title: 'Review is not complete', message: 'Enter an override reason to submit without completed review.' }],
          error: ''
        }));
      } else if (err?.response?.data?.code === 'NCF_RATES_REVIEW_REQUIRED') {
        setSubmitReadinessModal(previous => ({ ...previous, blockingIssues: [{ key: 'rates-review', title: 'Rates review is required', message: err.response.data.error }], error: '' }));
      } else if (err?.response?.data?.code === 'NCF_INCOMPLETE_ADDRESSES' && err.response.data.incompleteAddresses?.length) {
        setSubmitReadinessModal(previous => ({
          ...previous,
          blockingIssues: [addressReadinessIssue(err.response.data.incompleteAddresses.map(item => ({
            sourcePath: item.sourcePath,
            label: item.label,
            fieldGroup: `${item.fieldGroup || ''} - missing ${item.missing.join(', ')}`
          })))],
          error: ''
        }));
      } else if (err?.response?.data?.code === 'NCF_INVALID_CONTACT_FIELDS' && err.response.data.invalidFields?.length) {
        setSubmitReadinessModal(previous => ({
          ...previous,
          blockingIssues: [{
            key: 'contact-fields',
            title: 'Email addresses or phone numbers are invalid',
            message: err.response.data.error,
            fields: err.response.data.invalidFields.map(field => ({ ...field, fieldGroup: field.message }))
          }],
          error: ''
        }));
      } else if (err?.response?.data?.code === 'NCF_MISSING_REQUIRED_FIELDS' && serverMissingFields?.length) {
        setSubmitReadinessModal(previous => ({
          ...previous,
          blockingIssues: [{ key: 'fields', title: 'Required fields are incomplete', message: 'Fill in these required fields before submitting to HR.', fields: serverMissingFields }],
          error: ''
        }));
      } else {
        setSubmitReadinessModal(previous => ({ ...previous, error: err?.response?.data?.error || 'Could not submit the NCF to HR.' }));
      }
    } finally {
      setSubmittingToHr(false);
      setBusy(false);
    }
  };

  const uploadResumeAndSubmit = async () => {
    if (!resumeUploadFile) {
      setError('Upload the candidate resume before submitting to HR.');
      return;
    }

    setResumeUploadBusy(true);
    setError('');
    try {
      const response = await ncfAPI.addDocument(id, resumeUploadFile, 'resume');
      setData(previous => ({ ...previous, documents: response.data.documents || previous.documents }));
      setResumeUploadFile(null);
      setResumeUploadModal(false);
      await openSubmitReadinessModal({ resumeValidated: true });
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not upload the candidate resume.');
    } finally {
      setResumeUploadBusy(false);
    }
  };

  const handleChangeSubmitted = async (result) => {
    setChangeModalOpen(false);
    await load();
    const notification = result?.notification;
    setNoticeModal({
      show: true,
      title: 'Change submitted to HR',
      message: notification?.failedCount
        ? `${notification.sentCount} notification(s) sent; ${notification.failedCount} failed.`
        : `${notification?.sentCount || 0} HR notification(s) sent.`
    });
  };

  const notifyHr = async () => {
    setBusy(true);
    try {
      const response = await ncfAPI.notifyHr(id);
      const notification = response.data?.notification;
      setMessage(notification?.failedCount
        ? `${notification.sentCount} HR notification(s) sent; ${notification.failedCount} failed.`
        : `${notification?.sentCount || 0} HR notification(s) sent.`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not notify HR.');
    } finally { setBusy(false); }
  };

  const share = async () => {
    const recipients = shareRecipients.split(/[,;\n]/).map(value => value.trim()).filter(Boolean);
    if (recipients.length === 0) {
      setError('Enter at least one recipient email address.');
      return;
    }

    setShareBusy(true);
    try {
      await ncfAPI.share(id, { recipients });
      setMessage('NCF workbook emailed successfully.');
      setShareRecipients('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not email the NCF workbook.');
    } finally { setShareBusy(false); }
  };

  const reupload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setBusy(true);
    try {
      await ncfAPI.addDocument(id, file, documentType);
      await load();
      setMessage(`${documentType} document uploaded.`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not upload the document.');
    } finally { setBusy(false); }
  };

  const deleteDocument = async (document) => {
    const filename = document.originalFileName || document.OriginalFileName || 'this document';
    setConfirmModal({
      show: true,
      title: 'Delete Document',
      message: `Delete ${filename}?`,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, show: false });
        setBusy(true);
        try {
          await ncfAPI.deleteDocument(id, document.documentId || document.DocumentId);
          await load();
          setNoticeModal({
            show: true,
            title: 'Document Deleted',
            message: `${filename} deleted.`
          });
        } catch (err) {
          setError(err?.response?.data?.error || 'Could not delete the document.');
        } finally { setBusy(false); }
      }
    });
  };

  const deleteNcf = async () => {
    setConfirmModal({
      show: true,
      title: 'Delete NCF',
      message: `Delete ${candidateName || 'this NCF'}? This will remove the NCF and its documents.`,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, show: false });
        setBusy(true);
        try {
          await ncfAPI.remove(id);
          navigate('/ncfs');
        } catch (err) {
          setError(err?.response?.data?.error || 'Could not delete the NCF.');
        } finally { setBusy(false); }
      }
    });
  };

  if (loading) return <main className="page-container"><p>Loading NCF...</p></main>;
  if (!data?.ncf) return <main className="page-container"><div className="alert alert-error">{error || 'NCF not found.'}</div><Link to="/ncfs">Back to NCFs</Link></main>;

  const ncf = data.ncf;
  const isOwner = canWriteThisNcf && String(ncf.userId || ncf.UserId || '') === String(currentUserId);
  const assignedApproval = approvalWorkflow.approvals.find(approval => String(approval.reviewerUserId) === String(currentUserId));
  const isClearedForStart = ['cleared for start', 'cleared to start'].includes(
    String(statusDefinitions.find(definition => definition.code === persistedStatus)?.name || '').trim().toLowerCase()
  );
  const canChangeNcf = NCF_CHANGE_STATUSES.includes(persistedStatus) && canWriteThisNcf;
  const canEditReviewStatus = canManageAny ? persistedStatus !== 'cancelled' : persistedStatus === 'draft';
  const canEditReview = canEditReviewStatus && (canWriteThisNcf || (canApproveAssigned && assignedApproval?.status === 'pending'));
  const approvalsComplete = !approvalWorkflow.enabled || (approvalWorkflow.approvals.length > 0 && approvalWorkflow.approvals.every(approval => approval.status === 'approved'));
  const canSubmitToHr = false;
  const canSubmitToHrAction = persistedStatus === 'draft' && canWriteThisNcf;
  const canRequestApproval = persistedStatus === 'draft' && canWriteThisNcf;
  const canNotifyHr = persistedStatus === 'submitted' && canManageAny;
  const canEndEngagement = ['submitted', 'approved', 'onboarded', 'completed', 'ending'].includes(String(persistedStatus).toLowerCase()) && canWriteThisNcf
    && !statusDefinitions.some(definition => definition.code === persistedStatus && definition.name.trim().toLowerCase() === 'no start');
  const canComment = canWriteThisNcf || Boolean(assignedApproval) || (
    hasPermission('ncf:read:own') && Boolean(data?.access?.isAssignedRecruiterOrSalesperson)
  );
  const allCommentRecipientsSelected = Object.values(commentRecipientRoles).every(Boolean);
  const toggleAllCommentRecipients = () => {
    const nextValue = !allCommentRecipientsSelected;
    setCommentRecipientRoles({ recruiter: nextValue, salesperson: nextValue, submitter: nextValue, hr: nextValue });
  };
  const reviewerRoleLabel = (role) => (role === 'salesperson' ? 'Salesperson' : role === 'recruiter' ? 'Recruiter' : 'Additional reviewer');
  const activityFeed = [
    ...comments.map(item => ({ type: 'comment', at: item.createdAt, key: `comment-${item.commentId}`, author: item.authorName, body: item.body, recipients: item.recipients })),
    ...(approvalWorkflow.workflow ? [{ type: 'review_requested', at: approvalWorkflow.workflow.createdAt, key: `requested-${approvalWorkflow.workflow.workflowId}-${approvalWorkflow.workflow.updatedAt}`, customMessage: approvalWorkflow.workflow.customMessage, reviewers: approvalWorkflow.approvals }] : []),
    ...approvalWorkflow.approvals.filter(approval => approval.decidedAt).map(approval => ({ type: 'decision', at: approval.decidedAt, key: `decision-${approval.approvalId}`, role: approval.role, reviewerName: approval.reviewerName, status: approval.status, decisionNote: approval.decisionNote })),
    ...(approvalWorkflow.workflow?.overrideUsed ? [{ type: 'override', at: approvalWorkflow.workflow.updatedAt, key: `override-${approvalWorkflow.workflow.workflowId}`, reason: approvalWorkflow.workflow.overrideReason }] : [])
  ].sort((left, right) => new Date(left.at) - new Date(right.at));
  const renderFeedItem = (item) => {
    const when = item.at ? new Date(item.at).toLocaleString() : '';
    if (item.type === 'comment') {
      return <div className="ncf-comment-item" key={item.key}><div className="ncf-comment-item-header"><strong>{item.author || 'Someone'}</strong><small>{when}</small></div><p>{item.body}</p>{item.recipients?.length > 0 && <div className="ncf-comment-item-recipients">Notified: {item.recipients.map(recipient => recipient.name || recipient.email).join(', ')}</div>}</div>;
    }
    if (item.type === 'review_requested') {
      return <div className="ncf-comment-item ncf-comment-item-event" key={item.key}><div className="ncf-comment-item-header"><strong>Review requested</strong><small>{when}</small></div>{item.customMessage && <p>{item.customMessage}</p>}<div className="ncf-comment-item-recipients">Reviewers: {item.reviewers.length > 0 ? item.reviewers.map(approval => `${reviewerRoleLabel(approval.role)}: ${approval.reviewerName}`).join(', ') : 'None'}</div></div>;
    }
    if (item.type === 'decision') {
      return <div className={`ncf-comment-item ncf-comment-item-event ncf-comment-item-${item.status}`} key={item.key}><div className="ncf-comment-item-header"><strong>{item.reviewerName} {item.status === 'approved' ? 'approved' : 'rejected'} ({reviewerRoleLabel(item.role)})</strong><small>{when}</small></div>{item.decisionNote && <p>{item.decisionNote}</p>}</div>;
    }
    if (item.type === 'override') {
      return <div className="ncf-comment-item ncf-comment-item-event ncf-comment-item-override" key={item.key}><div className="ncf-comment-item-header"><strong>Review overridden</strong><small>{when}</small></div><p>{item.reason}</p></div>;
    }
    return null;
  };
  const currentStatus = persistedStatus || 'draft';
  const statusLabel = (code) => statusDefinitions.find(item => item.code === code)?.name || code;
  const availableStatusChanges = canManageAny
    ? statusDefinitions.filter(item => item.isActive && item.code !== currentStatus && item.code !== 'submitted')
    : [];
  const selectedStatusDefinition = statusDefinitions.find(item => item.code === status);
  const documents = data.documents || [];
  const history = data.history || [];
  const candidateName = `${ncf.candidateFirstName || ncf.CandidateFirstName || ''} ${ncf.candidateLastName || ncf.CandidateLastName || ''}`.trim();
  const generatedDocuments = documents.filter(document => (document.documentType || document.DocumentType) === 'generated_ncf');
  const generated = generatedDocuments.find(document => document.isCurrent ?? document.IsCurrent) || generatedDocuments[0];
  const submittedAt = ncf.reviewedData?.ncf?.submittedDate;
  const submittedAtLabel = submittedAt ? new Date(submittedAt).toLocaleString() : '';
  const persistedRoot = {
    ncf: {},
    assignment: {
      payRate: ncf.payRate ?? ncf.PayRate ?? null,
      payRateType: ncf.payRateType || ncf.PayRateType || null,
      desiredStartDate: ncf.desiredStartDate || ncf.DesiredStartDate || null,
      clientName: ncf.clientName || ncf.ClientName || null,
      jobTitle: ncf.jobDivaJobTitle || ncf.JobDivaJobTitle || null,
      jobReference: ncf.jobDivaData?.job?.reference || null,
      jobLocation: ncf.jobDivaData?.job?.location || null,
      jobStartDate: ncf.jobDivaData?.job?.startDate || null,
      jobEndDate: ncf.jobDivaData?.job?.endDate || null
    },
    jobdiva: ncf.jobDivaData || ncf.JobDivaData || {}
  };
  const reviewRoot = applySubmissionMetadataDefaults(applyReviewedValues(applyNonEmptyPaths(structuredClone(persistedRoot), ncf.extractedData), reviewData), ncf);
  synchronizePlacementInputs(reviewRoot, fieldInventory);
  try { applyPlacementCalculation(reviewRoot, fieldInventory); }
  catch (_error) { if (getValue(reviewRoot, FEE_MODE) === 'percentage') setPath(reviewRoot, FEE_AMOUNT, ''); }
  const permanentPlacement = isPermanentPlacement(reviewRoot);
  const currentFinancialSignature = financialSignature(reviewRoot, fieldInventory);
  const reviewedFinancialSignature = reviewData?.ncf?.ratesReviewSignature;
  const ratesAcknowledged = reviewedFinancialSignature === currentFinancialSignature
    && reviewData?.ncf?.ratesReviewUserId === String(currentUserId);
  const startDateFieldPath = findStartDateFieldPath(fieldInventory);
  const jobDivaStartDateValue = ncf.jobDivaData?.job?.startDate || ncf.JobDivaData?.job?.startDate || '';
  const jobDivaStartDateIsPast = Boolean(jobDivaStartDateValue) && isPastDateValue(getPath(reviewRoot, startDateFieldPath) || jobDivaStartDateValue);
  const overviewStartDateSourceValue = overviewStartDateEditing ? overviewStartDateValue : (ncf.desiredStartDate || ncf.DesiredStartDate);
  const ncfCreatedAtValue = ncf.createdAt || ncf.CreatedAt;
  const showOverviewStartDateWarning = String(currentStatus || '').toLowerCase() === 'draft'
    && isDateBeforeValue(overviewStartDateSourceValue, ncfCreatedAtValue);
  const ncfStartDateValue = ncf.desiredStartDate || ncf.DesiredStartDate;
  const startDateNeedsReview = (value) => String(currentStatus || '').toLowerCase() === 'draft'
    && (isDateBeforeValue(value, ncfCreatedAtValue) || isPastDateValue(value));
  const startDateReadinessIssue = (value) => ({
    key: 'start-date',
    title: isDateBeforeValue(value, ncfCreatedAtValue) ? 'Start date is before the NCF creation date' : 'Start date is in the past',
    message: 'Update the start date below if it is wrong, or acknowledge it if this date is expected.'
  });
  const setReviewField = (sourcePath, value) => updateFinancialFields({ [sourcePath]: value });
  // Mirrors the History page start date column: updates propagate to the Client/Project
  // Information "Start Date" review field below via setReviewField, without a full reload.
  const startEditingOverviewStartDate = () => {
    setOverviewStartDateValue(toDateInputValue(ncf.desiredStartDate || ncf.DesiredStartDate));
    setOverviewStartDateEditing(true);
  };

  const persistStartDate = async (value) => {
    const response = await ncfAPI.updateStartDate(id, value);
    setData(previous => ({ ...previous, ncf: { ...previous.ncf, ...response.data.ncf } }));
    // The server already stored this date in reviewed data; don't mark it dirty for autosave.
    setReviewData(previous => {
      const next = JSON.parse(JSON.stringify(previous || {}));
      setPath(next, startDateFieldPath, value);
      return next;
    });
  };

  const saveOverviewStartDate = async () => {
    if (!overviewStartDateValue) { setOverviewStartDateEditing(false); return; }
    setOverviewStartDateBusy(true);
    try {
      await persistStartDate(overviewStartDateValue);
      setOverviewStartDateEditing(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not update the start date.');
    } finally { setOverviewStartDateBusy(false); }
  };

  const saveReadinessStartDate = async () => {
    const value = submitReadinessModal.startDateValue;
    if (!isValidDateValue(value)) {
      setSubmitReadinessModal(previous => ({ ...previous, error: 'Enter a valid start date.' }));
      return;
    }
    setOverviewStartDateBusy(true);
    setSubmitReadinessModal(previous => ({ ...previous, error: '' }));
    try {
      await persistStartDate(value);
      const otherIssues = submitReadinessModal.overrideIssues.filter(issue => issue.key !== 'start-date');
      setSubmitReadinessModal(previous => ({
        ...previous,
        overrideIssues: startDateNeedsReview(value) ? [...otherIssues, startDateReadinessIssue(value)] : otherIssues,
        startDateOverrideAcknowledged: false,
        startDateSaved: true
      }));
    } catch (err) {
      setSubmitReadinessModal(previous => ({ ...previous, error: err?.response?.data?.error || 'Could not update the start date.' }));
    } finally { setOverviewStartDateBusy(false); }
  };

  const verifyOverviewStartDate = async () => {
    setOverviewStartDateBusy(true);
    try {
      const response = await ncfAPI.verifyStartDate(id);
      const updatedNcf = response.data.ncf;
      const updatedStatus = updatedNcf.status || updatedNcf.Status;
      setStatus(updatedStatus);
      setData(previous => ({ ...previous, ncf: { ...previous.ncf, ...updatedNcf, Status: updatedStatus } }));
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not verify the start date.');
    } finally { setOverviewStartDateBusy(false); }
  };

  // Uses the same schedule/notify-HR flow as the NCFs history page; both views update this record.
  const openContractEndModal = () => {
    setContractEndModal({
      show: true,
      value: toDateInputValue(ncf.contractEndDate || ncf.ContractEndDate),
      noStart: false,
      reason: '',
      busy: false,
      error: ''
    });
  };

  const closeContractEndModal = () => setContractEndModal({ show: false, value: '', reason: '', busy: false, error: '' });

  const submitContractEndDate = async () => {
    if (!contractEndModal.noStart && !contractEndModal.value) {
      setContractEndModal(previous => ({ ...previous, error: 'Enter a contract end date.' }));
      return;
    }
    if (!contractEndModal.reason.trim()) {
      setContractEndModal(previous => ({ ...previous, error: 'Enter a reason for the contract end.' }));
      return;
    }
    setContractEndModal(previous => ({ ...previous, busy: true, error: '' }));
    try {
      const response = await ncfAPI.updateContractEndDate(id, contractEndModal.noStart ? null : contractEndModal.value, contractEndModal.reason.trim(), Boolean(contractEndModal.noStart));
      setData(previous => ({ ...previous, ncf: { ...previous.ncf, ...response.data.ncf } }));
      closeContractEndModal();
      await load();
    } catch (err) {
      setContractEndModal(previous => ({ ...previous, busy: false, error: err?.response?.data?.error || 'Could not set the contract end date.' }));
    }
  };

  const addCommentRecipient = (option) => {
    if (!option?.raw || commentAdditionalRecipients.some(item => item.email.toLowerCase() === option.raw.email.toLowerCase())) return;
    setCommentAdditionalRecipients(previous => [...previous, option.raw]);
  };

  const postComment = async () => {
    const body = commentBody.trim();
    if (!body) return;
    setCommentBusy(true);
    try {
      const recipientRoles = Object.entries(commentRecipientRoles).filter(([, enabled]) => enabled).map(([role]) => role);
      const response = await ncfAPI.addComment(id, { body, recipientRoles, additionalRecipients: commentAdditionalRecipients });
      setComments(response.data.comments || []);
      setCommentBody('');
      setCommentRecipientRoles({ recruiter: false, salesperson: false, submitter: false, hr: false });
      setCommentAdditionalRecipients([]);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not post the comment.');
    } finally { setCommentBusy(false); }
  };

  const isFilled = (value) => value !== null && value !== undefined
    && String(value).trim() !== ''
    && !/^select(?:\.\.\.|…)$/.test(String(value).trim().toLowerCase());
  const getFieldRequired = (field) => {
    const inventoryEntry = fieldInventory.find(inv => inv.sourcePath === field.sourcePath);
    if (!inventoryEntry) return field.required ?? false;
    return isFieldEffectivelyRequired(inventoryEntry, reviewRoot);
  };
  const isFieldComplete = (field, value) => (field.format === 'date'
    ? isValidDateValue(value)
    : isFilled(value) && !getFieldValidationError(field.format, value));

  // Groups the admin-configured field inventory into form sections, ordered by each section's
  // earliest field (so admins control section order purely via each field's sort order).
  const reviewSections = (() => {
    const bySection = new Map();
    fieldInventory.forEach(field => {
      const key = field.fieldGroup || 'Other';
      if (!bySection.has(key)) bySection.set(key, { title: key, fields: [], minSortOrder: field.sortOrder ?? 0 });
      const section = bySection.get(key);
      section.fields.push(field);
      section.minSortOrder = Math.min(section.minSortOrder, field.sortOrder ?? 0);
    });
    return Array.from(bySection.values()).sort((a, b) => a.minSortOrder - b.minSortOrder);
  })();
  const visibleReviewSections = reviewSections.filter(section => {
    const visibility = sectionVisibility.find(item => item.sectionName === section.title);
    if (!visibility || visibility.visibilityMode === 'always') return true;
    const matches = evaluateRequirementRules(visibility.visibilityRules, reviewRoot);
    return visibility.visibilityMode === 'hideWhen' ? !matches : matches;
  });
  const arrangedReviewSections = ratesLayout(visibleReviewSections, permanentPlacement);
  const updateFinancialFields = patch => {
    setReviewData(previous => {
      const next = JSON.parse(JSON.stringify(previous || {}));
      const fullPatch = { ...patch, ...linkedCorporationPatch(buildNcfReviewRoot(ncf, previous), patch) };
      Object.entries(fullPatch).forEach(([path, value]) => setPath(next, path, value));
      const effective = buildNcfReviewRoot(ncf, next);
      if (isPermanentPlacement(effective)) {
        if (Object.prototype.hasOwnProperty.call(patch, FEE_PERCENT)) {
          setPath(next, FEE_PERCENT_UNIT, 'percent');
          setPath(effective, FEE_PERCENT_UNIT, 'percent');
        }
        synchronizePlacementInputs(effective, fieldInventory);
        try { applyPlacementCalculation(effective, fieldInventory); }
        catch (_error) { if (getValue(effective, FEE_MODE) === 'percentage') setPath(effective, FEE_AMOUNT, ''); }
        PLACEMENT_DERIVED_PATHS.forEach(path => { if (getValue(effective, path) !== undefined) setPath(next, path, getValue(effective, path)); });
      }
      return next;
    });
    reviewDataReadyRef.current = true;
    reviewDataDirtyRef.current = true;
    reviewDataVersionRef.current += 1;
  };

  // Keep conditional groups in a stable position so toggling a checkbox only expands its details.
  const buildFieldGroups = (fields) => {
    const dependentsByTrigger = new Map();
    const standalone = [];
    fields.forEach(field => {
      const condition = getSimpleEqualityCondition(field);
      const triggerField = condition && fields.find(f => f.sourcePath === condition.sourcePath);
      if (triggerField && triggerField.format === 'boolean') {
        const list = dependentsByTrigger.get(triggerField.sourcePath) || [];
        list.push(field);
        dependentsByTrigger.set(triggerField.sourcePath, list);
      } else {
        standalone.push(field);
      }
    });
    const groups = standalone.map(field => ({ field, dependents: dependentsByTrigger.get(field.sourcePath) || null }));
    groups.sort((a, b) => {
      // Uses effective requirement so conditionally required fields move up only while their condition is met.
      const priority = group => (getFieldRequired(group.field) ? 0 : group.dependents ? 1 : 2);
      return priority(a) - priority(b) || (a.field.sortOrder ?? 0) - (b.field.sortOrder ?? 0);
    });
    return groups;
  };

  const getMissingRequiredFields = () => visibleReviewSections.flatMap(section => section.fields
    .filter(field => getFieldRequired(field) && !isFieldComplete(field, getPath(reviewRoot, field.sourcePath)))
    .map(field => ({ sourcePath: field.sourcePath, label: field.label, fieldGroup: section.title })));
  const getIncompleteAddresses = async () => {
    const items = visibleReviewSections.flatMap(section => section.fields
      .filter(field => field.format === 'address' && getFieldRequired(field))
      .map(field => ({ field, section, value: String(getPath(reviewRoot, field.sourcePath) ?? '').trim() })))
      .filter(item => item.value);
    const results = await Promise.all(items.map(item => validateAddress(item.value)));
    return items
      .map((item, index) => ({ ...item, result: results[index] }))
      .filter(item => item.result && !item.result.complete)
      .map(item => ({ sourcePath: item.field.sourcePath, label: item.field.label, fieldGroup: `${item.section.title} - ${describeAddressIssue(item.result)}` }));
  };
  const jobDivaAddressRoot = buildJobDivaAddressRoot(ncf);
  const incompleteJobDivaAddresses = jobDivaAddressChecks
    .filter(item => getFieldRequired(item.field))
    .filter(item => String(getPath(reviewRoot, item.field.sourcePath) ?? '').trim() === item.value);
  const renderField = (field) => {
    const fieldValue = getPath(reviewRoot, field.sourcePath);
    const value = fieldValue == null ? '' : String(fieldValue);
    const inputId = `ncf-${field.sourcePath.replace(/[^a-z0-9]+/gi, '-')}`;
    const isRequired = getFieldRequired(field);
    const isComplete = isFieldComplete(field, fieldValue);
    const validationError = getFieldValidationError(field.format, fieldValue);
    const validationProps = {
      'aria-invalid': Boolean(validationError) || (isRequired && !isComplete),
      'aria-describedby': validationError ? `${inputId}-error` : undefined
    };
    const startDateWarning = field.sourcePath === startDateFieldPath && jobDivaStartDateIsPast
      ? <span className="ncf-start-date-warning">JobDiva start date is before today.</span>
      : null;
    const label = <>{field.label}{isRequired && <span className={`ncf-required-mark ${isComplete ? 'complete' : 'incomplete'}`}>{isComplete ? 'Complete' : 'Required'}</span>}{startDateWarning}</>;

    if (field.format === 'boolean') {
      // A boolean field with Options configured is really a single-select choice (e.g. who
      // provides equipment) rendered as a labeled checkbox per option instead of a plain Yes/No toggle.
      if (field.options?.length > 0) {
        return <div className="ncf-boolean-options-row" key={field.sourcePath}>
          <span className="ncf-boolean-options-label">{label}</span>
          <div className="ncf-boolean-options-choices">
            {field.options.map(option => {
              const optionId = `${inputId}-${option.replace(/[^a-z0-9]+/gi, '-')}`;
              const isChecked = value === option;
              return <label key={option} htmlFor={optionId} className="ncf-boolean-option-checkbox">
                <input id={optionId} type="checkbox" checked={isChecked} onChange={() => setReviewField(field.sourcePath, isChecked ? '' : option)} disabled={!canEditReview} />
                <span>{option}</span>
              </label>;
            })}
          </div>
        </div>;
      }
      const checked = [true, 'true', 'yes', 'y', '1'].includes(typeof fieldValue === 'string' ? fieldValue.toLowerCase() : fieldValue);
      const linkedCorporation = field.sourcePath === C2C_OWNERSHIP_PATH ? String(getPath(reviewRoot, C2C_LINKED_NAME_PATH) || '').trim() : '';
      const checkbox = <label className="ncf-boolean-field-row" key={field.sourcePath} htmlFor={inputId}><input id={inputId} type="checkbox" checked={checked} onChange={(event) => setReviewField(field.sourcePath, event.target.checked ? 'Yes' : 'No')} disabled={!canEditReview} /><span>{label}</span></label>;
      return linkedCorporation
        ? <div key={field.sourcePath} className="ncf-linked-corporation-field">{checkbox}<span className="ncf-linked-corporation-hint">JobDiva linked corporation: <strong>{linkedCorporation}</strong></span></div>
        : checkbox;
    }

    const controlClassName = validationError || (isRequired && !isComplete) ? 'ncf-required-field-empty' : undefined;
    if (field.format === 'address') {
      const jobDivaValue = String(getPath(jobDivaAddressRoot, field.sourcePath) ?? '').trim();
      return <label key={field.sourcePath} htmlFor={inputId}>{label}<AddressInput id={inputId} className={controlClassName} value={value} onChange={(nextValue) => setReviewField(field.sourcePath, nextValue)} disabled={!canEditReview} required={isRequired} fromJobDiva={Boolean(jobDivaValue) && value.trim() === jobDivaValue} /></label>;
    }
    const dateSyncProps = field.sourcePath === startDateFieldPath ? { onBlur: (event) => syncCandidateStartDate(event.target.value) } : {};
    const htmlInputType = field.format === 'date' ? 'date' : field.format === 'email' ? 'email' : field.format === 'tel' ? 'tel' : 'text';
    const input = <label key={field.sourcePath} htmlFor={inputId}>{label}{field.options?.length > 0 ? <select id={inputId} className={controlClassName} value={isFilled(fieldValue) ? value : ''} {...validationProps} onChange={(event) => setReviewField(field.sourcePath, event.target.value)} disabled={!canEditReview}><option value="">Select...</option>{field.options.map(option => <option key={option} value={option}>{option}</option>)}</select> : field.isMultiline ? <textarea id={inputId} className={controlClassName} value={value} {...validationProps} onChange={(event) => setReviewField(field.sourcePath, event.target.value)} rows="3" disabled={!canEditReview} /> : <input id={inputId} className={controlClassName} type={htmlInputType} value={value} {...validationProps} onChange={(event) => setReviewField(field.sourcePath, event.target.value)} disabled={!canEditReview} {...dateSyncProps} />}{validationError && <small id={`${inputId}-error`} role="alert">{validationError}</small>}</label>;
    return field.sourcePath === 'jobdiva.job.timesheetApproverName'
      ? <div key={field.sourcePath} className="ncf-approver-field">{input}<NcfSameAsManager root={reviewRoot} disabled={!canEditReview} onCopy={updateFinancialFields} /></div>
      : input;
  };
  const renderFieldGroup = ({ field, dependents }) => {
    if (!dependents) return renderField(field);
    const fieldValue = getPath(reviewRoot, field.sourcePath);
    const checked = [true, 'true', 'yes', 'y', '1'].includes(typeof fieldValue === 'string' ? fieldValue.toLowerCase() : fieldValue);
    return <div className={`ncf-boolean-group${checked ? ' is-expanded' : ''}`} key={field.sourcePath}>
      <div className="ncf-boolean-group-header">
        {renderField(field)}
        <span className="ncf-boolean-group-summary">{checked ? `${dependents.length} required detail${dependents.length === 1 ? '' : 's'}` : `${dependents.length} additional detail${dependents.length === 1 ? '' : 's'}`}</span>
      </div>
      {checked && <div className="ncf-boolean-group-fields" aria-live="polite">{dependents.map(renderField)}</div>}
    </div>;
  };
  // One row of the Rates panel: label + amount input + a frequency dropdown that shares its
  // value with the paired OT rate (Bill Rate/OT Bill Rate, Pay Rate/OT Pay Rate).
  const renderRateRow = (field) => {
    const fieldValue = getPath(reviewRoot, field.sourcePath);
    const value = fieldValue == null ? '' : String(fieldValue);
    const inputId = `ncf-${field.sourcePath.replace(/[^a-z0-9]+/gi, '-')}`;
    const isRequired = getFieldRequired(field);
    const isComplete = isFieldComplete(field, fieldValue);
    const controlClassName = isRequired && !isComplete ? 'ncf-required-field-empty' : undefined;
    const ratePerPath = getRateKind(field)?.perPath;
    const ratePerValue = String(getPath(reviewRoot, ratePerPath) || '').toLowerCase() || 'hour';
    return <div className="ncf-rate-row" key={field.sourcePath}>
      <label htmlFor={inputId} className="ncf-rate-row-label">{field.label}{isRequired && <span className={`ncf-required-mark ${isComplete ? 'complete' : 'incomplete'}`}>{isComplete ? 'Complete' : 'Required'}</span>}</label>
      <input id={inputId} className={controlClassName} type="text" value={value} onChange={(event) => setReviewField(field.sourcePath, event.target.value)} disabled={!canEditReview} />
      <select
        className="ncf-rate-row-per"
        value={ratePerValue}
        onChange={(event) => setReviewField(ratePerPath, event.target.value)}
        disabled={!canEditReview}
        aria-label={`${field.label} frequency`}
      >
        {RATE_PER_OPTIONS.map(option => <option key={option} value={option}>{formatRateUnit(option)}</option>)}
      </select>
    </div>;
  };
  const renderRatesPanelContent = (layout, { onAcknowledge } = {}) => {
    const { isRatesHost, rateFields, submittalFields } = layout;
    return <>
      <h4 className="ncf-rates-panel-title">Rates</h4>
      <div className="ncf-rates-table">{rateFields.map(renderRateRow)}</div>
      {permanentPlacement && isRatesHost && <>
        <div className="ncf-review-grid">{submittalFields.filter(field => !WORKSHEET_PATHS.includes(field.sourcePath)).map(renderField)}</div>
        <NcfPlacementWorksheet root={reviewRoot} fields={fieldInventory} onChange={updateFinancialFields} disabled={!canEditReview} />
      </>}
      {isRatesHost && <label className="ncf-rates-acknowledgement"><input type="checkbox" checked={ratesAcknowledged} disabled={!canEditReview || busy} onChange={event => (onAcknowledge
        ? onAcknowledge(event.target.checked)
        : updateFinancialFields({ 'ncf.ratesReviewSignature': event.target.checked ? currentFinancialSignature : '', 'ncf.ratesReviewUserId': event.target.checked ? String(currentUserId) : '' }))} />I have reviewed the rates, frequencies and placement fees.<span className="ncf-required-mark">Required</span></label>}
    </>;
  };
  const renderSection = (section) => {
    const layout = arrangedReviewSections.find(item => item.title === section.title);
    if (!layout) return null;
    const { isRatesHost, rateFields, otherFields, submittalFields } = layout;
    const groups = buildFieldGroups(otherFields);
    const requiredCount = [...rateFields, ...otherFields, ...submittalFields].filter(field => getFieldRequired(field)).length;
    const collapsed = Boolean(collapsedSections[section.title]);
    return <section className="ncf-review-section" key={section.title}>
      <button type="button" className="ncf-review-section-header" onClick={() => setCollapsedSections(previous => ({ ...previous, [section.title]: !previous[section.title] }))} aria-expanded={!collapsed}>
        <h3>{section.title}</h3>
        <span className="ncf-review-section-meta">{requiredCount > 0 && <span className="ncf-review-section-required-badge">{requiredCount} required</span>}<span className="ncf-review-section-toggle-icon">{collapsed ? '▸' : '▾'}</span></span>
      </button>
      {!collapsed && (isRatesHost || rateFields.length > 0) && <div className="ncf-rates-panel">{renderRatesPanelContent(layout)}</div>}
      {!collapsed && <div className="ncf-review-grid">{groups.map(renderFieldGroup)}</div>}
    </section>;
  };

  const download = async (document) => {
    if (!document) return;
    const response = await ncfAPI.download(id, document.documentId || document.DocumentId);
    const url = URL.createObjectURL(response.data);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.originalFileName || document.OriginalFileName || 'NCF.xlsx';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Shared between the Review tab and Comments tab so the pending-approval status list is
  // always visible no matter which tab the submitter is on; the send/resend button itself
  // lives in the persistent top action bar, right before Submit to HR.
  const renderApprovalWorkflowPanel = () => (
    approvalWorkflow.enabled ? <div className="ncf-approval-list">{approvalWorkflow.approvals.filter(approval => approval.status === 'pending').map(approval => {
      const reviewerCanAct = String(approval.reviewerUserId) === String(currentUserId);
      return <div className="ncf-approval-row" key={approval.approvalId}><div><strong>{reviewerRoleLabel(approval.role)}</strong><span>{approval.reviewerName} ({approval.reviewerEmail})</span></div><span className={`ncf-approval-status ncf-approval-status-${approval.status}`}>{approval.status}</span>{reviewerCanAct && <div><button type="button" className="btn btn-primary btn-sm" onClick={() => { setApprovalDecisionNote(''); setApprovalDecisionModal({ role: approval.role, decision: 'approved' }); }} disabled={busy}>Approve</button><button type="button" className="btn btn-danger btn-sm" onClick={() => { setApprovalDecisionNote(''); setApprovalDecisionModal({ role: approval.role, decision: 'rejected' }); }} disabled={busy}>Reject</button></div>}</div>;
    })}</div> : null
  );

  const renderSubmitReadinessModal = () => {
    if (!submitReadinessModal.show) return null;
    const activeBlockingIssues = getActiveBlockingIssues();
    const hasBlockingIssues = activeBlockingIssues.length > 0;
    const ratesHostLayout = arrangedReviewSections.find(layout => layout.isRatesHost);
    const hasReviewOverride = submitReadinessModal.overrideIssues.some(issue => issue.key === 'review');
    const hasStartDateOverride = submitReadinessModal.overrideIssues.some(issue => issue.key === 'start-date');
    const canSubmitFromModal = !hasBlockingIssues
      && !submitReadinessModal.recipientsLoading
      && !busy
      && (!hasReviewOverride || submitReadinessModal.reviewOverrideReason.trim())
      && (!hasStartDateOverride || submitReadinessModal.startDateOverrideAcknowledged);
    return <div className="modal-overlay" onClick={() => !busy && setSubmitReadinessModal(previous => ({ ...previous, show: false }))}>
      <div className="modal-content ncf-submit-readiness-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Submit to HR</h2>
            <p>Review everything that affects this submission in one place.</p>
          </div>
          <button type="button" onClick={() => setSubmitReadinessModal(previous => ({ ...previous, show: false }))} className="close-btn" disabled={busy}>✕</button>
        </div>

        {hasBlockingIssues ? <div className="ncf-readiness-summary ncf-readiness-summary-blocked">This NCF is not ready to submit yet.</div> : <div className="ncf-readiness-summary ncf-readiness-summary-ready">No blocking issues found.</div>}
        {submitReadinessModal.error && <div className="alert alert-error">{submitReadinessModal.error}</div>}
        {submitReadinessModal.startDateSaved && <div className="alert alert-success">Start date updated to {formatDisplayDate(ncfStartDateValue)}.</div>}

        {submitReadinessModal.blockingIssues.length > 0 && <section className="ncf-readiness-section">
          <h3>Must be fixed before submitting</h3>
          <div className="ncf-readiness-issue-list">{submitReadinessModal.blockingIssues.map(issue => <div className={`ncf-readiness-issue ${activeBlockingIssues.includes(issue) ? 'is-blocking' : 'is-warning'}`} key={issue.key}>
            <strong>{issue.title}{!activeBlockingIssues.includes(issue) && ' ✓'}</strong>
            <p>{issue.message}</p>
            {issue.fields?.length > 0 && <ul className="ncf-missing-fields-list">{issue.fields.map(field => <li key={field.sourcePath}><strong>{field.label}</strong> <small>{field.fieldGroup}</small></li>)}</ul>}
            {issue.key === 'rates-review' && ratesHostLayout && <div className="ncf-rates-panel">{renderRatesPanelContent(ratesHostLayout, { onAcknowledge: acknowledgeRatesFromReadiness })}</div>}
            {issue.key === 'resume' && <div className="ncf-readiness-upload-row">
              <input type="file" accept=".pdf,.docx" onChange={(event) => setSubmitReadinessModal(previous => ({ ...previous, resumeFile: event.target.files?.[0] || null, error: '' }))} disabled={submitReadinessModal.resumeUploading} />
              <button type="button" className="btn btn-secondary" onClick={uploadResumeFromReadiness} disabled={submitReadinessModal.resumeUploading || !submitReadinessModal.resumeFile}>{submitReadinessModal.resumeUploading ? 'Uploading...' : 'Upload resume'}</button>
            </div>}
          </div>)}</div>
        </section>}

        {submitReadinessModal.overrideIssues.length > 0 && <section className="ncf-readiness-section">
          <h3>Can be overridden</h3>
          <div className="ncf-readiness-issue-list">{submitReadinessModal.overrideIssues.map(issue => <div className="ncf-readiness-issue is-warning" key={issue.key}>
            <strong>{issue.title}</strong>
            <p>{issue.message}</p>
            {issue.key === 'review' && issue.allowNotRequired && <label className="ncf-readiness-checkbox"><input type="checkbox" checked={submitReadinessModal.reviewOverrideReason.includes(REVIEW_NOT_REQUIRED_TEXT)} onChange={(event) => toggleReviewNotRequired(event.target.checked)} disabled={busy} /> Review not required</label>}
            {issue.key === 'review' && <label className="ncf-readiness-override-field">Override reason<textarea value={submitReadinessModal.reviewOverrideReason} onChange={(event) => setSubmitReadinessModal(previous => ({ ...previous, reviewOverrideReason: event.target.value, error: '' }))} rows="3" placeholder="Explain why this NCF is being submitted without completed review." /></label>}
            {issue.key === 'start-date' && <div className="ncf-readiness-upload-row">
              <label className="ncf-readiness-override-field">Start date
                <input type="date" value={submitReadinessModal.startDateValue} onChange={(event) => setSubmitReadinessModal(previous => ({ ...previous, startDateValue: event.target.value, error: '' }))} disabled={busy || overviewStartDateBusy || !(isOwner || canManageAny)} aria-label="Start date" />
              </label>
              {(isOwner || canManageAny) && <button type="button" className="btn btn-secondary" onClick={saveReadinessStartDate} disabled={busy || overviewStartDateBusy || !submitReadinessModal.startDateValue || submitReadinessModal.startDateValue === toDateInputValue(ncfStartDateValue)}>{overviewStartDateBusy ? 'Saving...' : 'Update start date'}</button>}
            </div>}
            {issue.key === 'start-date' && <label className="ncf-readiness-checkbox"><input type="checkbox" checked={submitReadinessModal.startDateOverrideAcknowledged} onChange={(event) => setSubmitReadinessModal(previous => ({ ...previous, startDateOverrideAcknowledged: event.target.checked, error: '' }))} /> I reviewed the start date and want to submit anyway.</label>}
          </div>)}</div>
        </section>}

        {!hasBlockingIssues && <section className="ncf-readiness-section">
          <h3>HR notification</h3>
          {submitReadinessModal.recipientsLoading ? <p>Resolving HR notification recipients...</p> : <p>The following address{submitReadinessModal.recipients.length === 1 ? '' : 'es'} will receive the HR notification: <strong>{submitReadinessModal.recipients.length > 0 ? submitReadinessModal.recipients.join(', ') : 'none'}</strong></p>}
        </section>}

        <section className="ncf-readiness-section">
          <label className="ncf-readiness-override-field">
            Comments (optional)
            <textarea
              value={submitReadinessModal.submitterComment}
              onChange={(event) => setSubmitReadinessModal(previous => ({ ...previous, submitterComment: event.target.value, error: '' }))}
              rows="4"
              maxLength={2000}
              placeholder="Add comments to include with this submission."
              disabled={busy}
            />
          </label>
        </section>

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setSubmitReadinessModal(previous => ({ ...previous, show: false }))} disabled={busy}>Cancel</button>
          {hasBlockingIssues ? <button type="button" className="btn btn-primary" onClick={() => setSubmitReadinessModal(previous => ({ ...previous, show: false }))} disabled={busy}>Review issues</button> : <button type="button" className="btn btn-primary" onClick={submitFromReadinessModal} disabled={!canSubmitFromModal}>{submittingToHr ? 'Submitting...' : 'Submit to HR'}</button>}
        </div>
      </div>
    </div>;
  };

  return (
    <main className="page-container ncf-detail-page">
      {renderSubmitReadinessModal()}
      {reviewRequestModalOpen && <NcfReviewRequestModal
        onClose={() => setReviewRequestModalOpen(false)}
        onSend={requestApprovals}
        reviewers={approvalReviewers}
        selections={approvalSelections}
        setSelections={setApprovalSelections}
        enabledRoles={approvalRoleEnabled}
        setEnabledRoles={setApprovalRoleEnabled}
        reviewNotNecessary={reviewNotNecessary}
        setReviewNotNecessary={setReviewNotNecessary}
        additionalRecipients={additionalApprovalRecipients}
        setAdditionalRecipients={setAdditionalApprovalRecipients}
        customMessage={approvalCustomMessage}
        setCustomMessage={setApprovalCustomMessage}
        searchRecipients={searchApprovalRecipients}
        createRecipient={createTypedEmailRecipient}
        error={reviewRequestError}
      />}
      <div className="page-header">
        <div>
          <Link to="/ncfs">Back to NCFs</Link>
          <h1>{candidateName || 'NCF detail'}</h1>
          <p>{ncf.candidateEmail || ncf.CandidateEmail}</p>
          {submittedAtLabel && <p className="ncf-submitted-at">Submitted to HR: {submittedAtLabel}</p>}
        </div>
      </div>
      <div className="ncf-detail-action-bar">
        <span className={`status-badge status-${currentStatus}`}>{statusLabel(currentStatus)}</span>
        <div className="ncf-detail-action-buttons">
          {canSubmitToHr && <button type="button" className="btn btn-primary" onClick={() => submitToHr()} disabled={busy}>{submittingToHr && <span className="ncf-button-spinner" aria-hidden="true" />}<span>{submittingToHr ? 'Submitting...' : 'Submit to HR'}</span></button>}
          {canChangeNcf && <button type="button" className="btn btn-primary" onClick={() => setChangeModalOpen(true)} disabled={busy}>Change</button>}
          {canNotifyHr && <button type="button" className="btn btn-secondary" onClick={notifyHr} disabled={busy}>Notify HR</button>}
          {canEndEngagement && <button type="button" className="btn btn-secondary" onClick={openContractEndModal} disabled={busy} title="End Engagement">End</button>}
          {canSubmitToHrAction && <>
            <button type="button" className="btn btn-secondary" onClick={() => { setReviewNotNecessary(false); setApprovalRequestModal(true); }} disabled={busy || approvalLoading}>{approvalWorkflow.enabled ? 'Resend for review' : 'Send for review'}</button>
            <button type="button" className="btn btn-primary" onClick={() => submitToHr()} disabled={busy || approvalLoading}>{submittingToHr ? 'Submitting...' : 'Submit to HR'}</button>
          </>}
          {canWriteThisNcf && (canManageAny || persistedStatus === 'draft') && <button type="button" className="btn btn-danger" onClick={deleteNcf} disabled={busy}>Delete NCF</button>}
        </div>
      </div>
      {reviewSendConfirmation && <p className="ncf-review-send-confirmation" role="status">{reviewSendConfirmation}</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-info">{message}</div>}
      {confirmModal.show && <div className="modal-overlay" onClick={() => setConfirmModal({ ...confirmModal, show: false })}><div className="modal-content" onClick={(e) => e.stopPropagation()}><div className="modal-header"><h2>{confirmModal.title}</h2><button onClick={() => setConfirmModal({ ...confirmModal, show: false })} className="close-btn">✕</button></div><p>{confirmModal.message}</p><div className="modal-actions"><button type="button" onClick={() => setConfirmModal({ ...confirmModal, show: false })} className="btn btn-secondary">Cancel</button><button type="button" onClick={confirmModal.onConfirm} className="btn btn-primary" disabled={busy}>Confirm</button></div></div></div>}
      {noticeModal.show && <div className="modal-overlay" onClick={() => setNoticeModal({ ...noticeModal, show: false })}><div className="modal-content" onClick={(e) => e.stopPropagation()}><div className="modal-header"><h2>{noticeModal.title}</h2><button onClick={() => setNoticeModal({ ...noticeModal, show: false })} className="close-btn">✕</button></div><p>{noticeModal.message}</p><div className="modal-actions"><button type="button" onClick={() => setNoticeModal({ ...noticeModal, show: false })} className="btn btn-primary">OK</button></div></div></div>}
      {resumeUploadModal && <div className="modal-overlay" onClick={() => !resumeUploadBusy && setResumeUploadModal(false)}><div className="modal-content" onClick={(event) => event.stopPropagation()}><div className="modal-header"><h2>Resume required</h2><button type="button" onClick={() => setResumeUploadModal(false)} className="close-btn" disabled={resumeUploadBusy}>✕</button></div><p>Upload the candidate resume before submitting this NCF to HR.</p><label className="ncf-resume-upload-label">Candidate resume<input type="file" accept=".pdf,.docx" onChange={(event) => setResumeUploadFile(event.target.files?.[0] || null)} disabled={resumeUploadBusy} /></label>{resumeUploadFile && <p className="form-hint">Selected: {resumeUploadFile.name}</p>}<div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setResumeUploadModal(false)} disabled={resumeUploadBusy}>Cancel</button><button type="button" className="btn btn-primary" onClick={uploadResumeAndSubmit} disabled={resumeUploadBusy || !resumeUploadFile}>{resumeUploadBusy ? 'Uploading...' : 'Upload and submit'}</button></div></div></div>}
      {missingFieldsModal.show && <div className="modal-overlay" onClick={() => setMissingFieldsModal({ show: false, fields: [] })}><div className="modal-content" onClick={(e) => e.stopPropagation()}><div className="modal-header"><h2>Missing required fields</h2><button onClick={() => setMissingFieldsModal({ show: false, fields: [] })} className="close-btn">✕</button></div><p>{`Fill in the following required field${missingFieldsModal.fields.length === 1 ? '' : 's'} before submitting to HR:`}</p><ul className="ncf-missing-fields-list">{missingFieldsModal.fields.map(field => <li key={field.sourcePath}><strong>{field.label}</strong> <small>{field.fieldGroup}</small></li>)}</ul><div className="modal-actions"><button type="button" onClick={() => setMissingFieldsModal({ show: false, fields: [] })} className="btn btn-primary">OK</button></div></div></div>}
      {reviewGateModal.show && <div className="modal-overlay" onClick={() => setReviewGateModal({ show: false, reason: null })}><div className="modal-content" onClick={(event) => event.stopPropagation()}><div className="modal-header"><h2>Review not complete</h2><button type="button" onClick={() => setReviewGateModal({ show: false, reason: null })} className="close-btn">✕</button></div><p>{reviewGateModal.reason === 'pending' ? 'This NCF has been sent for review, but not everyone has approved it yet.' : "This NCF hasn't been sent for review yet."}</p><p>You can send it for review and wait for approval, or submit to HR now and record why the review is being skipped.</p><div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setReviewGateModal({ show: false, reason: null })}>Cancel</button><button type="button" className="btn btn-secondary" onClick={() => { setReviewGateModal({ show: false, reason: null }); setReviewNotNecessary(false); setApprovalRequestModal(true); }}>Send for review</button><button type="button" className="btn btn-danger" onClick={() => { setReviewGateModal({ show: false, reason: null }); openApprovalOverrideModal(); }} disabled={hrRecipientsLoading}>Submit anyway</button></div></div></div>}
      {approvalRequestModal && <div className="modal-overlay" onClick={() => setApprovalRequestModal(false)}><div className="modal-content" onClick={(event) => event.stopPropagation()}><div className="modal-header"><h2>Send NCF for review</h2><button type="button" onClick={() => setApprovalRequestModal(false)} className="close-btn">✕</button></div><label className="ncf-review-not-needed"><input type="checkbox" checked={reviewNotNecessary} onChange={(event) => { const checked = event.target.checked; setReviewNotNecessary(checked); if (checked) { setApprovalSelections({ recruiter: '', salesperson: '' }); setAdditionalApprovalRecipients([]); } }} /> Review not necessary</label><p>Select the record-linked reviewers who should approve this NCF.</p>{['recruiter', 'salesperson'].map(role => { const reviewer = approvalReviewers.find(item => String(item.userId) === String(approvalSelections[role])); return <label key={role} className="ncf-reviewer-checkbox"><input type="checkbox" checked={Boolean(approvalSelections[role])} disabled={reviewNotNecessary || !reviewer} onChange={(event) => { setError(''); setApprovalSelections(previous => ({ ...previous, [role]: event.target.checked ? reviewer?.userId || '' : '' })); }} /><span>{role === 'salesperson' ? 'Salesperson' : 'Recruiter'}{reviewer ? `: ${reviewer.name} (${reviewer.email})` : ' (no matching directory user)'}</span></label>; })}<label className="ncf-additional-reviewers"><span>Additional recipient (optional)</span></label><Typeahead value={null} onSelect={addAdditionalApprovalRecipient} fetchOptions={searchApprovalRecipients} createOption={createTypedEmailRecipient} minChars={2} disabled={reviewNotNecessary} placeholder="Search users or groups, or type an email..." />{additionalApprovalRecipients.length > 0 && <div className="ncf-additional-recipient-list">{additionalApprovalRecipients.map(recipient => <div className="ncf-additional-recipient-row" key={recipient.email}><span>{recipient.name} ({recipient.email})</span><button type="button" className="btn btn-danger btn-sm" onClick={() => setAdditionalApprovalRecipients(previous => previous.filter(item => item.email !== recipient.email))} disabled={reviewNotNecessary}>Remove</button></div>)}</div>}<label className="ncf-review-message"><span>Message to recipients (optional)</span><textarea value={approvalCustomMessage} onChange={(event) => setApprovalCustomMessage(event.target.value)} disabled={reviewNotNecessary} rows="4" maxLength="2000" placeholder="Add a message for the reviewers..." /></label>{reviewRequestError && <div className="alert alert-error">{reviewRequestError}</div>}<div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setApprovalRequestModal(false)}>Cancel</button><button type="button" className="btn btn-primary" onClick={requestApprovals} disabled={busy}>{reviewNotNecessary ? 'Save' : 'Send'}</button></div></div></div>}
      {approvalDecisionModal && <div className="modal-overlay" onClick={() => setApprovalDecisionModal(null)}><div className="modal-content" onClick={(event) => event.stopPropagation()}><div className="modal-header"><h2>{approvalDecisionModal.decision === 'approved' ? 'Approve NCF' : 'Reject NCF'}</h2><button type="button" onClick={() => setApprovalDecisionModal(null)} className="close-btn">✕</button></div><p>Add a note for this decision.</p><textarea value={approvalDecisionNote} onChange={(event) => setApprovalDecisionNote(event.target.value)} rows="4" placeholder={approvalDecisionModal.decision === 'rejected' ? 'Rejection reason' : 'Optional approval note'} /><div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setApprovalDecisionModal(null)}>Cancel</button><button type="button" className={approvalDecisionModal.decision === 'approved' ? 'btn btn-primary' : 'btn btn-danger'} onClick={decideApproval} disabled={busy}>Confirm</button></div></div></div>}
      {approvalOverrideModal && <div className="modal-overlay" onClick={() => setApprovalOverrideModal(false)}><div className="modal-content" onClick={(event) => event.stopPropagation()}><div className="modal-header"><h2>Submit to HR anyway</h2><button type="button" onClick={() => setApprovalOverrideModal(false)} className="close-btn">✕</button></div><p>This bypasses pending approvals and will be recorded in the NCF history. Enter a reason.</p><p className="ncf-recipient-preview"><strong>HR notification emails:</strong> {hrNotificationRecipients.join(', ') || 'None'}</p><textarea value={approvalOverrideReason} onChange={(event) => setApprovalOverrideReason(event.target.value)} rows="4" placeholder="Override reason" /><div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setApprovalOverrideModal(false)}>Cancel</button><button type="button" className="btn btn-danger" onClick={overrideAndSubmit} disabled={busy}>Submit anyway</button></div></div></div>}
      {changeModalOpen && <NcfChangeModal ncfId={id} ncf={ncf} fieldInventory={fieldInventory} sectionVisibility={sectionVisibility} onClose={() => setChangeModalOpen(false)} onSubmitted={handleChangeSubmitted} />}
      {viewingChanges && <div className="modal-overlay" onClick={() => setViewingChanges(null)}><div className="modal-content ncf-update-modal" onClick={(event) => event.stopPropagation()}><div className="modal-header"><h2>Submission changes</h2><button onClick={() => setViewingChanges(null)} className="close-btn">✕</button></div><p><strong>{viewingChanges.submissionReason}</strong></p>{viewingChanges.changeNote && <p className="ncf-submission-note"><strong>Explanation:</strong> {viewingChanges.changeNote}</p>}<div className="ncf-change-list">{viewingChanges.changes.map(change => <div key={change.sourcePath}><strong>{change.label}</strong><small>{change.fieldGroup}</small><span>{change.previousValue} <b>to</b> {change.currentValue}</span></div>)}</div><div className="modal-actions"><button type="button" className="btn btn-primary" onClick={() => setViewingChanges(null)}>Close</button></div></div></div>}
      {contractEndModal.show && <NcfContractEndModal state={contractEndModal} setState={setContractEndModal} onClose={closeContractEndModal} onSubmit={submitContractEndDate} isPastDate={isContractEndDatePast} canNoStart={canEndEngagement} />}
      <section className="content-section ncf-detail-section"><h2>Overview</h2><div className="ncf-detail-grid"><div><strong>Client</strong><span>{ncf.clientName || ncf.ClientName || '-'}</span></div><div><strong>JobDiva job</strong><span>{ncf.jobDivaJobTitle || ncf.jobDivaJobId || ncf.JobDivaJobTitle || ncf.JobDivaJobId || '-'}</span></div><div><strong>JobDiva candidate</strong><span>{ncf.jobDivaCandidateName || ncf.jobDivaCandidateId || ncf.JobDivaCandidateName || ncf.JobDivaCandidateId || '-'}</span></div><div><strong>Start date</strong>{overviewStartDateEditing ? <span className="ncf-start-date-edit"><input type="date" value={overviewStartDateValue} onChange={(event) => setOverviewStartDateValue(event.target.value)} disabled={overviewStartDateBusy} aria-label="Start date" />{showOverviewStartDateWarning && <span className="ncf-start-date-warning-icon" title="Start date is before this NCF was created" aria-label="Start date is before this NCF was created">⚠</span>}<button type="button" className="btn btn-secondary" onClick={saveOverviewStartDate} disabled={overviewStartDateBusy}>Save</button><button type="button" className="btn btn-link" onClick={() => setOverviewStartDateEditing(false)} disabled={overviewStartDateBusy}>Cancel</button></span> : <span className="ncf-start-date-display">{(isOwner || canManageAny) ? <button type="button" className="ncf-start-date-value" onClick={startEditingOverviewStartDate} title="Click to change the start date">{formatDisplayDate(ncf.desiredStartDate || ncf.DesiredStartDate)}</button> : formatDisplayDate(ncf.desiredStartDate || ncf.DesiredStartDate)}{showOverviewStartDateWarning && <span className="ncf-start-date-warning-icon" title="Start date is before this NCF was created" aria-label="Start date is before this NCF was created">⚠</span>}{(ncf.startDateVerified || ncf.StartDateVerified) ? <span className="ncf-start-date-verified" title={`Verified${(ncf.startDateVerifiedByName || ncf.StartDateVerifiedByName) ? ` by ${ncf.startDateVerifiedByName || ncf.StartDateVerifiedByName}` : ''}${(ncf.startDateVerifiedAt || ncf.StartDateVerifiedAt) ? ` on ${formatDisplayDate(ncf.startDateVerifiedAt || ncf.StartDateVerifiedAt)}` : ''}`}>✓</span> : ((isOwner || canManageAny) && isClearedForStart) ? <button type="button" className="ncf-verify-btn" onClick={verifyOverviewStartDate} disabled={overviewStartDateBusy}>Verify start date</button> : null}</span>}</div></div></section>
      <nav className="ncf-detail-tabs" aria-label="NCF detail sections">{[['review', 'Review'], ['comments', 'Comments'], ['documents', 'Documents'], ['history', 'History']].map(([value, label]) => <button type="button" key={value} className={activeTab === value ? 'active' : ''} onClick={() => setActiveTab(value)}>{label}</button>)}</nav>
      {activeTab === 'review' && <section className="content-section ncf-detail-section">{renderApprovalWorkflowPanel()}{assignedApproval && assignedApproval.status === 'pending' && <div className="ncf-approval-list ncf-my-approval-banner"><div className="ncf-approval-row" key={assignedApproval.approvalId}><div><strong>Your review is requested</strong><span>{reviewerRoleLabel(assignedApproval.role)} approval for this NCF</span></div><span className={`ncf-approval-status ncf-approval-status-${assignedApproval.status}`}>{assignedApproval.status}</span><div><button type="button" className="btn btn-primary btn-sm" onClick={() => { setApprovalDecisionNote(''); setApprovalDecisionModal({ role: assignedApproval.role, decision: 'approved' }); }} disabled={busy}>Approve</button><button type="button" className="btn btn-danger btn-sm" onClick={() => { setApprovalDecisionNote(''); setApprovalDecisionModal({ role: assignedApproval.role, decision: 'rejected' }); }} disabled={busy}>Reject</button></div></div></div>}<div className="section-heading"><div><h2>Candidate data</h2></div>{canEditReview && <div className="ncf-review-save-actions">{autoSaveStatus && <span className="ncf-autosave-status">{autoSaveStatus}</span>}<button className="btn btn-secondary" type="button" onClick={saveReview} disabled={busy}>Save candidate data</button></div>}</div><div className="ncf-review-panel-stack">{incompleteJobDivaAddresses.length > 0 && <div className="alert alert-warning ncf-address-alert" role="status"><strong>Some addresses from JobDiva are incomplete.</strong> The JobDiva values were kept; please complete them before submitting to HR.<ul>{incompleteJobDivaAddresses.map(item => <li key={item.field.sourcePath}><strong>{item.field.label}</strong> ({item.field.fieldGroup}): {describeAddressIssue(item.result)}</li>)}</ul></div>}{visibleReviewSections.map(renderSection)}</div></section>}
      {activeTab === 'comments' && <section className="content-section ncf-detail-section"><div className="section-heading"><div><h2>Comments &amp; review</h2><p>Discuss this NCF and manage recruiter/salesperson review here.</p></div></div>{renderApprovalWorkflowPanel()}<div className="ncf-comment-feed">{activityFeed.length === 0 ? <p>No comments or review activity yet.</p> : activityFeed.map(renderFeedItem)}</div>{canComment && <div className="ncf-comment-composer"><label className="ncf-comment-recipients"><span>Notify <button type="button" className="btn-link ncf-comment-select-all" onClick={toggleAllCommentRecipients}>{allCommentRecipientsSelected ? 'Deselect all' : 'Select all'}</button></span><span className="ncf-comment-recipient-options"><label><input type="checkbox" checked={commentRecipientRoles.recruiter} onChange={(event) => setCommentRecipientRoles(previous => ({ ...previous, recruiter: event.target.checked }))} /> Recruiter<small className="ncf-comment-recipient-hint">{getCommentReviewerInfo('recruiter')?.name || 'Not set'}</small></label><label><input type="checkbox" checked={commentRecipientRoles.salesperson} onChange={(event) => setCommentRecipientRoles(previous => ({ ...previous, salesperson: event.target.checked }))} /> Salesperson<small className="ncf-comment-recipient-hint">{getCommentReviewerInfo('salesperson')?.name || 'Not set'}</small></label><label><input type="checkbox" checked={commentRecipientRoles.submitter} onChange={(event) => setCommentRecipientRoles(previous => ({ ...previous, submitter: event.target.checked }))} /> Submitter<small className="ncf-comment-recipient-hint">{ncf.ownerName || ncf.OwnerName || ncf.ownerEmail || ncf.OwnerEmail || 'Not set'}</small></label><label><input type="checkbox" checked={commentRecipientRoles.hr} onChange={(event) => setCommentRecipientRoles(previous => ({ ...previous, hr: event.target.checked }))} /> NCF admin email<small className="ncf-comment-recipient-hint">{hrNotificationRecipients.length > 0 ? hrNotificationRecipients.join(', ') : 'Not set'}</small></label></span></label><Typeahead value={null} onSelect={addCommentRecipient} fetchOptions={searchApprovalRecipients} createOption={createTypedEmailRecipient} minChars={2} placeholder="Add another recipient..." />{commentAdditionalRecipients.length > 0 && <div className="ncf-additional-recipient-list">{commentAdditionalRecipients.map(recipient => <div className="ncf-additional-recipient-row" key={recipient.email}><span>{recipient.name} ({recipient.email})</span><button type="button" className="btn btn-danger btn-sm" onClick={() => setCommentAdditionalRecipients(previous => previous.filter(item => item.email !== recipient.email))}>Remove</button></div>)}</div>}<textarea value={commentBody} onChange={(event) => setCommentBody(event.target.value)} rows="3" placeholder="Add a comment..." disabled={commentBusy} /><div className="ncf-comment-composer-actions"><button type="button" className="btn btn-primary" onClick={postComment} disabled={commentBusy || !commentBody.trim()}>{commentBusy ? 'Posting...' : 'Post comment'}</button></div></div>}</section>}
      {activeTab === 'documents' && <section className="content-section ncf-detail-section"><div className="section-heading"><div><h2>Documents</h2><p>Uploaded source documents and generated workbook history.</p></div>{canWriteThisNcf && <div className="ncf-document-upload"><select value={documentType} onChange={(event) => setDocumentType(event.target.value)}><option value="resume">Resume</option><option value="bgc">BGC form</option><option value="legal_id">Legal ID</option><option value="job_description">Job description</option><option value="other">Other</option></select><label className="btn btn-secondary">Upload<input type="file" accept=".pdf,.docx,.png,.jpg,.jpeg" onChange={reupload} hidden /></label></div>}</div>{jobDivaResumeLoading && <div className="alert alert-info">Checking JobDiva for the candidate resume...</div>}{canWriteThisNcf && ncf.jobDivaCandidateId && !jobDivaResumeLoading && !documents.some(document => String(document.documentType || document.DocumentType || '').toLowerCase() === 'resume') && <div className="alert alert-warning">No resume was retrieved from JobDiva. Upload the candidate resume before submitting this NCF to HR.</div>}<ul className="ncf-document-list">{documents.map(document => { const type = document.documentType || document.DocumentType; const documentId = document.documentId || document.DocumentId; const filename = document.originalFileName || document.OriginalFileName || 'Document'; return <li key={documentId}><span>{filename} <small>{type}{document.version ? ` • v${document.version}` : ''}{document.isCurrent ? ' • Current' : ''}</small></span><div className="ncf-document-actions">{canReadDocuments && <button className="btn btn-secondary" type="button" onClick={() => download(document)}>Download</button>}{canWriteThisNcf && <button className="btn btn-danger" type="button" onClick={() => deleteDocument(document)} disabled={busy}>Delete</button>}</div></li>; })}</ul></section>}
      {activeTab === 'generate' && <section className="content-section ncf-detail-section"><h2>Generate and share</h2>{canWriteThisNcf && <div className="ncf-workflow"><button className="btn btn-primary" type="button" onClick={generate} disabled={busy}>Generate NCF Excel</button>{generated && canReadDocuments && <button className="btn btn-secondary" type="button" onClick={() => download(generated)}>Download latest Excel</button>}</div>}{canWriteThisNcf && <div className="ncf-share-form"><label>Recipients<input value={shareRecipients} onChange={(event) => setShareRecipients(event.target.value)} placeholder="email@example.com, another@example.com" /></label><button className="btn btn-secondary" type="button" onClick={share} disabled={shareBusy}>Email latest workbook</button></div>}</section>}
      {activeTab === 'history' && <section className="content-section ncf-detail-section"><h2>Submission history</h2>{submissionVersions.length > 0 && <ul className="ncf-submission-timeline">{submissionVersions.map(version => <li key={version.submissionVersionId}><div><strong>Version {version.versionNumber}: {version.submissionKind === 'initial' ? 'Initial submission' : 'Update'}</strong><span>{version.submissionReason}</span>{version.changeNote && <span className="ncf-submission-note"><strong>Explanation:</strong> {version.changeNote}</span>}<small>{new Date(version.submittedAt).toLocaleString()} {version.submittedByName ? `by ${version.submittedByName}` : ''}</small></div>{version.changes.length > 0 && <button type="button" className="btn btn-secondary" onClick={() => setViewingChanges(version)}>View changes ({version.changes.length})</button>}</li>)}</ul>}<h2>Status history</h2>{canManageAny && availableStatusChanges.length > 0 && <div className="ncf-status-change-panel"><div className="ncf-workflow"><select value={status} onChange={(event) => setStatus(event.target.value)}><option value={currentStatus}>{statusLabel(currentStatus)}</option>{availableStatusChanges.map(item => <option key={item.code} value={item.code}>{item.name}</option>)}</select><input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional status note" /><button className="btn btn-secondary" type="button" onClick={updateStatus} disabled={busy || status === currentStatus}>Update status</button></div>{status !== currentStatus && selectedStatusDefinition?.notificationEnabled && <p className="ncf-status-notification-notice">This status change will add a comment and notify its configured recipients.</p>}</div>}<ul className="ncf-history-list">{history.map((event, index) => <li key={event.HistoryId || event.historyId || index}><strong>{statusLabel(event.ToStatus || event.toStatus)}</strong> <span className="ncf-submission-note">{event.Note || event.note || ''}</span> <small>{event.CreatedAt || event.createdAt ? new Date(event.CreatedAt || event.createdAt).toLocaleString() : ''}</small></li>)}</ul></section>}
    </main>
  );
}

export default NcfDetail;
